import Sidebar from '@/components/layout/sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import type { Team, UserWithTeam } from '@shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building,
  Calendar,
  Mail,
  Search,
  Settings,
  Shield,
  UserPlus,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Switch } from '@/components/ui/switch';

/** Split legacy mixed hod_ids into HOD vs DPM using user roles; merge explicit dpm_ids. */
function splitTeamLeadIdsFromTeam(
  teamAny: any,
  users: UserWithTeam[]
): { hodIds: string[]; hodId: string; dpmIds: string[]; dpmId: string } {
  const rawHod: string[] = Array.isArray(teamAny.hodIds)
    ? (teamAny.hodIds as string[])
    : teamAny.hodId
      ? [teamAny.hodId as string]
      : [];
  const rawDpm: string[] = Array.isArray(teamAny.dpmIds)
    ? (teamAny.dpmIds as string[])
    : teamAny.dpmId
      ? [teamAny.dpmId as string]
      : [];
  const hodOnly: string[] = [];
  const dpmOnly: string[] = [...new Set(rawDpm)];
  for (const id of rawHod) {
    const u = users.find(x => x.id === id);
    if (u?.role === 'dpm') dpmOnly.push(id);
    else hodOnly.push(id);
  }
  const uniqHod = [...new Set(hodOnly)];
  const uniqDpm = [...new Set(dpmOnly)];
  return {
    hodIds: uniqHod,
    hodId: uniqHod[0] || '',
    dpmIds: uniqDpm,
    dpmId: uniqDpm[0] || '',
  };
}

export default function TeamManagement() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
  const [isEditTeamModalOpen, setIsEditTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithTeam | null>(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isCredsModalOpen, setIsCredsModalOpen] = useState(false);
  const [isTeamsModalOpen, setIsTeamsModalOpen] = useState(false);
  const [isProjectsModalOpen, setIsProjectsModalOpen] = useState(false);
  const [pendingPrimaryTeam, setPendingPrimaryTeam] = useState<string | 'none'>(
    'none'
  );
  const [pendingExtraTeams, setPendingExtraTeams] = useState<string[]>([]);
  const [pendingCities, setPendingCities] = useState<string[]>([]); // For PMs (multiple cities)
  const [pendingProjects, setPendingProjects] = useState<string[]>([]); // For managing user's projects
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const selectAllProjectsRef = useRef<HTMLInputElement>(null); // Ref for select all checkbox in projects modal

  // Create member modal state
  const [isCreateMemberOpen, setIsCreateMemberOpen] = useState(false);
  const [memberForm, setMemberForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    teamId: 'none' as 'none' | string,
    cityId: 'none' as 'none' | string, // For GM (single city)
    cityIds: [] as string[], // For PM and HOD (multiple cities)
    role: 'hod' as
      | 'team_member'
      | 'pm'
      | 'gm'
      | 'hod'
      | 'dpm'
      | 'manager'
      | 'admin'
      | 'staff', // Default to HOD
    allowTeamView: false,
    assignedByPmId: 'none' as 'none' | string, // PM to assign staff under
    assignedByHodId: 'none' as 'none' | string, // HOD to assign staff under
  });

  // Edit user modal state
  const [editUserForm, setEditUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    teamId: 'none' as 'none' | string,
    cityId: 'none' as 'none' | string, // For GM / staff (single city)
    cityIds: [] as string[], // For PM and HOD (multiple cities)
    role: 'hod' as
      | 'team_member'
      | 'pm'
      | 'gm'
      | 'hod'
      | 'dpm'
      | 'manager'
      | 'admin'
      | 'staff',
    allowTeamView: false,
    assignedByPmId: 'none' as 'none' | string, // Staff: leader (PM)
    assignedByHodId: 'none' as 'none' | string, // Staff: leader (HOD)
    isActive: true,
  });

  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deactivationImpact, setDeactivationImpact] = useState<{
    pendingCount: number;
    pendingTasks: { id: string; title: string; status: string }[];
  } | null>(null);

  // Form states
  const [teamForm, setTeamForm] = useState({
    name: '',
    description: '',
    managerId: '',
    pmId: '', // Keep for backward compatibility
    cityId: '', // Single city per project
    pmIds: [] as string[], // Array of PM IDs
    gmId: '', // GM included in this project (optional)
    hodIds: [] as string[], // Array of HOD IDs (multiple HODs)
    hodId: '', // Keep for backward compatibility
    dpmIds: [] as string[], // Array of DPM IDs
    dpmId: '', // Keep for backward compatibility
  });

  // City management state
  const [isCreateCityOpen, setIsCreateCityOpen] = useState(false);
  const [isEditCityOpen, setIsEditCityOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<any | null>(null);
  const [cityForm, setCityForm] = useState({
    name: '',
    description: '',
    gmId: '',
  });

  // Search state
  const [citiesSearch, setCitiesSearch] = useState('');
  const [projectsSearch, setProjectsSearch] = useState('');
  const [membersSearch, setMembersSearch] = useState('');

  // Redirect if not authenticated or insufficient permissions
  useEffect(() => {
    if (!authLoading && !user) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      setTimeout(() => {
        window.location.href = '/api/login';
      }, 500);
    } else if (
      !authLoading &&
      user &&
      !['pm', 'gm', 'hod', 'dpm', 'admin'].includes(user.role)
    ) {
      toast({
        title: 'Access Denied',
        description: "You don't have permission to access this page.",
        variant: 'destructive',
      });
    }
  }, [user, authLoading, toast]);

  const isAdmin = user?.role === 'admin';

  // Fetch data (admins load inactive users too for account management)
  const { data: users = [], isLoading: usersLoading } = useQuery<
    UserWithTeam[]
  >({
    queryKey: ['/api/users', isAdmin ? 'full' : 'active'],
    queryFn: async () => {
      const path = isAdmin
        ? '/api/users?includeInactive=true'
        : '/api/users';
      const res = await apiRequest('GET', path);
      return res.json();
    },
    retry: false,
    enabled:
      user?.role === 'manager' ||
      user?.role === 'admin' ||
      user?.role === 'pm' ||
      user?.role === 'hod' ||
      user?.role === 'dpm',
  });

  const { data: teams = [], isLoading: teamsLoading } = useQuery<Team[]>({
    queryKey: ['/api/teams'],
    retry: false,
  });

  const { data: cities = [], isLoading: citiesLoading } = useQuery<any[]>({
    queryKey: ['/api/cities'],
    retry: false,
    enabled:
      user?.role === 'admin' ||
      user?.role === 'gm' ||
      user?.role === 'hod' ||
      user?.role === 'dpm' ||
      user?.role === 'pm',
  });

  // Debounced username for availability check (Add Team Member)
  const [debouncedUsernameCheck, setDebouncedUsernameCheck] = useState('');
  useEffect(() => {
    const raw = (memberForm.username ?? '').trim().toLowerCase();
    if (raw.length < 3) {
      setDebouncedUsernameCheck('');
      return;
    }
    const t = setTimeout(() => setDebouncedUsernameCheck(raw), 400);
    return () => clearTimeout(t);
  }, [memberForm.username]);

  const { data: usernameAvailability, isFetching: usernameCheckLoading } =
    useQuery<{ available: boolean }>({
      queryKey: ['check-username', debouncedUsernameCheck],
      queryFn: async () => {
        const res = await apiRequest(
          'GET',
          `/api/users/check-username?username=${encodeURIComponent(debouncedUsernameCheck)}`
        );
        return res.json();
      },
      enabled: isCreateMemberOpen && debouncedUsernameCheck.length >= 3,
      staleTime: 60_000,
    });

  const isManager = user?.role === 'pm';
  const isGM = user?.role === 'gm';
  // Determine manager's team: prefer explicit managerId, else fall back to user's own teamId
  const managedTeam =
    isManager && teams.length
      ? teams.find(t => (t as any).managerId === user!.id) ||
        teams.find(t => t.id === user!.teamId) ||
        null
      : null;
  const myTeamId = isManager ? managedTeam?.id || user?.teamId || null : null;

  // Team Members filter state
  const [membersTeamFilter, setMembersTeamFilter] = useState<
    'all' | 'none' | string
  >('all');
  const [membersCityFilter, setMembersCityFilter] = useState<'all' | string>(
    'all'
  );

  // Projects filter state
  const [projectsCityFilter, setProjectsCityFilter] = useState<'all' | string>(
    'all'
  );

  // Update indeterminate state for select all checkbox in projects modal
  useEffect(() => {
    if (isProjectsModalOpen && selectedUser && selectAllProjectsRef.current) {
      // Calculate selection state
      let userCityIds: string[] = [];
      if (selectedUser.role === 'gm') {
        if ((selectedUser as any).cityId) {
          userCityIds = [(selectedUser as any).cityId];
        }
      } else if (
        selectedUser.role === 'pm' ||
        selectedUser.role === 'hod' ||
        selectedUser.role === 'dpm'
      ) {
        if ((selectedUser as any).cityIds) {
          if (typeof (selectedUser as any).cityIds === 'string') {
            try {
              userCityIds = JSON.parse((selectedUser as any).cityIds);
            } catch (e) {
              userCityIds = [];
            }
          } else if (Array.isArray((selectedUser as any).cityIds)) {
            userCityIds = (selectedUser as any).cityIds;
          }
        }
        if (userCityIds.length === 0 && (selectedUser as any).cityId) {
          userCityIds = [(selectedUser as any).cityId];
        }
      }

      const availableProjects = teams.filter((team: any) => {
        if (!team.cityId) return false;
        return userCityIds.includes(team.cityId);
      });

      const allSelected =
        availableProjects.length > 0 &&
        availableProjects.every((p: any) => pendingProjects.includes(p.id));
      const someSelected = availableProjects.some((p: any) =>
        pendingProjects.includes(p.id)
      );

      selectAllProjectsRef.current.indeterminate = someSelected && !allSelected;
    } else if (selectAllProjectsRef.current) {
      selectAllProjectsRef.current.indeterminate = false;
    }
  }, [isProjectsModalOpen, selectedUser, pendingProjects, teams]);

  useEffect(() => {
    if (isAdmin) {
      setMembersTeamFilter(prev =>
        prev === 'all' || prev === 'none' || teams.find(t => t.id === prev)
          ? prev
          : 'all'
      );
    } else if (isManager) {
      if (myTeamId) setMembersTeamFilter(myTeamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isManager, myTeamId, teams.length]);

  // Create team mutation
  const createTeamMutation = useMutation({
    mutationFn: async (teamData: typeof teamForm) => {
      await apiRequest('POST', '/api/teams', teamData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      setIsCreateTeamModalOpen(false);
      setTeamForm({
        name: '',
        description: '',
        managerId: '',
        pmId: '',
        cityId: '',
        pmIds: [],
        gmId: '',
        hodIds: [],
        hodId: '',
        dpmIds: [],
        dpmId: '',
      });
      toast({
        title: 'Success',
        description: 'Team created successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to create team',
        variant: 'destructive',
      });
    },
  });

  // Create member mutation
  const createMemberMutation = useMutation({
    mutationFn: async (data: typeof memberForm) => {
      const payload: any = {
        firstName: data.firstName.trim() || null,
        lastName: data.lastName.trim() || null,
        email: data.email.trim() || null,
        password: data.password,
        role: isManager ? 'team_member' : data.role,
        allowTeamView: data.role === 'team_member' ? data.allowTeamView : false,
      };
      const trimmedUsername = (data.username ?? '').trim();
      if (trimmedUsername) payload.username = trimmedUsername.toLowerCase();
      if (data.teamId !== 'none') payload.teamId = data.teamId;
      // For PM and HOD: send cityIds array, for GM/Staff: send cityId
      if (
        (data.role === 'pm' || data.role === 'hod' || data.role === 'dpm') &&
        data.cityIds.length > 0
      ) {
        payload.cityIds = data.cityIds;
        payload.cityId = data.cityIds[0]; // Keep first for backward compatibility
      } else if (
        (data.role === 'gm' || data.role === 'staff') &&
        data.cityId !== 'none'
      ) {
        payload.cityId = data.cityId;
      }
      // For staff: send assignedByPmId or assignedByHodId
      if (data.role === 'staff') {
        if (data.assignedByPmId !== 'none') {
          payload.assignedByPmId = data.assignedByPmId;
        }
        if (data.assignedByHodId !== 'none') {
          payload.assignedByHodId = data.assignedByHodId;
        }
      }
      await apiRequest('POST', '/api/users', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/cities'] });
      setIsCreateMemberOpen(false);
      setMemberForm({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        teamId: 'none',
        cityId: 'none',
        cityIds: [],
        role: 'hod',
        allowTeamView: false,
        assignedByPmId: 'none',
        assignedByHodId: 'none',
      });
      toast({
        title: 'Success',
        description: 'User created successfully',
      });
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      const msg = error?.message ?? '';
      if (msg.startsWith('409:')) {
        try {
          const body = JSON.parse(msg.slice(4).trim());
          toast({
            title: 'Cannot create member',
            description:
              body?.message ??
              'Username or email already registered. Please change the username or email and try again.',
            variant: 'destructive',
          });
        } catch {
          toast({
            title: 'Cannot create member',
            description:
              'Username or email already registered. Please change the username or email and try again.',
            variant: 'destructive',
          });
        }
        return;
      }
      const statusJson = msg.match(/^(\d{3}):\s*(\{[\s\S]*\})\s*$/);
      if (statusJson) {
        try {
          const body = JSON.parse(statusJson[2] as string) as {
            message?: string;
          };
          if (body?.message) {
            toast({
              title: 'Error',
              description: body.message,
              variant: 'destructive',
            });
            return;
          }
        } catch {
          // fall through
        }
      }
      toast({
        title: 'Error',
        description: 'Failed to create member',
        variant: 'destructive',
      });
    },
  });

  // Edit team mutation
  const editTeamMutation = useMutation({
    mutationFn: async ({
      teamId,
      teamData,
    }: {
      teamId: string;
      teamData: typeof teamForm;
    }) => {
      // Format the payload to ensure hodIds and pmIds are properly sent as arrays
      const payload: any = {
        name: teamData.name,
        description: teamData.description || '',
        cityId: teamData.cityId || '',
        managerId: teamData.managerId || '',
        gmId: teamData.gmId || '',
      };

      // Handle PM IDs - send as array
      if (Array.isArray(teamData.pmIds) && teamData.pmIds.length > 0) {
        payload.pmIds = teamData.pmIds;
        payload.pmId = teamData.pmIds[0]; // Keep first for backward compatibility
        payload.managerId = teamData.pmIds[0]; // Set managerId to first PM
      } else if (teamData.pmId) {
        payload.pmIds = [teamData.pmId];
        payload.pmId = teamData.pmId;
        payload.managerId = teamData.pmId;
      } else {
        payload.pmIds = [];
        payload.pmId = '';
      }

      // Handle HOD IDs - send as array
      if (Array.isArray(teamData.hodIds) && teamData.hodIds.length > 0) {
        payload.hodIds = teamData.hodIds;
        payload.hodId = teamData.hodIds[0]; // Keep first for backward compatibility
      } else if (teamData.hodId) {
        payload.hodIds = [teamData.hodId];
        payload.hodId = teamData.hodId;
      } else {
        payload.hodIds = [];
        payload.hodId = '';
      }

      if (Array.isArray(teamData.dpmIds) && teamData.dpmIds.length > 0) {
        payload.dpmIds = teamData.dpmIds;
        payload.dpmId = teamData.dpmIds[0];
      } else if (teamData.dpmId) {
        payload.dpmIds = [teamData.dpmId];
        payload.dpmId = teamData.dpmId;
      } else {
        payload.dpmIds = [];
        payload.dpmId = '';
      }

      await apiRequest('PATCH', `/api/teams/${teamId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      setIsEditTeamModalOpen(false);
      setEditingTeam(null);
      setTeamForm({
        name: '',
        description: '',
        managerId: '',
        pmId: '',
        cityId: '',
        pmIds: [],
        gmId: '',
        hodIds: [],
        hodId: '',
        dpmIds: [],
        dpmId: '',
      });
      toast({
        title: 'Success',
        description: 'Team updated successfully',
      });
    },
    onError: (error: any) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }

      // Handle specific error about active tasks
      let errorMessage = 'Failed to update team';

      // apiRequest throws errors in format "400: {json}"
      if (error?.message) {
        const messageParts = error.message.split(': ');
        if (messageParts.length > 1) {
          try {
            const errorData = JSON.parse(messageParts.slice(1).join(': '));
            if (errorData.errors && Array.isArray(errorData.errors)) {
              errorMessage = errorData.errors.join('\n');
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch {
            // If parsing fails, use the original message
            errorMessage = error.message;
          }
        } else {
          errorMessage = error.message;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
        duration: 6000, // Show longer for important errors
      });
    },
  });

  // Delete team mutation
  const deleteTeamMutation = useMutation({
    mutationFn: async (teamId: string) => {
      await apiRequest('DELETE', `/api/teams/${teamId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      toast({
        title: 'Success',
        description: 'Team deleted successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to delete team',
        variant: 'destructive',
      });
    },
  });

  // Update user role mutation
  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      await apiRequest('PATCH', `/api/users/${userId}/role`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      setIsEditUserModalOpen(false);
      setSelectedUser(null);
      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update user role',
        variant: 'destructive',
      });
    },
  });

  // Update user mutation (comprehensive edit)
  const updateUserMutation = useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: typeof editUserForm;
    }) => {
      // Update password separately if provided (using dedicated endpoint)
      if (data.password && data.password.length > 0) {
        await apiRequest('PATCH', `/api/users/${userId}/password`, {
          newPassword: data.password,
        });
      }

      // Update username separately if provided (using dedicated endpoint)
      if (data.username && data.username.trim().length > 0) {
        const uname = data.username.trim().toLowerCase();
        await apiRequest('PATCH', `/api/users/${userId}/username`, {
          username: uname,
        });
      }

      // Update other user fields
      const payload: any = {
        firstName: data.firstName.trim() || null,
        lastName: data.lastName.trim() || null,
        email: data.email.trim() || null,
        role: data.role,
      };

      if (data.isActive !== undefined) {
        payload.isActive = data.isActive;
      }

      // Staff: leader (PM/HOD) and city only; no primary team
      if (data.role === 'staff') {
        payload.teamId = null;
        if (data.cityId !== 'none') payload.cityId = data.cityId;
        else payload.cityId = null;
        payload.assignedByPmId =
          (data as any).assignedByPmId !== 'none'
            ? (data as any).assignedByPmId
            : null;
        payload.assignedByHodId =
          (data as any).assignedByHodId !== 'none'
            ? (data as any).assignedByHodId
            : null;
      } else {
        // Update team assignment if applicable (non-staff)
        if (data.teamId !== 'none') {
          payload.teamId = data.teamId;
        } else {
          payload.teamId = null;
        }

        // Update city assignments based on role
        if (
          (data.role === 'pm' || data.role === 'hod' || data.role === 'dpm') &&
          data.cityIds.length > 0
        ) {
          payload.cityIds = data.cityIds;
          payload.cityId = data.cityIds[0]; // Keep first for backward compatibility
        } else if (data.role === 'gm' && data.cityId !== 'none') {
          payload.cityId = data.cityId;
        } else {
          payload.cityId = null;
          if (data.role === 'pm' || data.role === 'hod' || data.role === 'dpm')
            payload.cityIds = [];
        }
      }

      await apiRequest('PATCH', `/api/users/${userId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/cities'] });
      setIsEditUserModalOpen(false);
      setSelectedUser(null);
      setEditUserForm({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        teamId: 'none',
        cityId: 'none',
        cityIds: [],
        role: 'hod',
        allowTeamView: false,
        assignedByPmId: 'none',
        assignedByHodId: 'none',
        isActive: true,
      });
      setDeactivateConfirmOpen(false);
      setDeactivationImpact(null);
      toast({
        title: 'Success',
        description: 'User updated successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update user',
        variant: 'destructive',
      });
    },
  });

  // Update username mutation (admin only)
  const updateUsernameMutation = useMutation({
    mutationFn: async ({
      userId,
      username,
    }: {
      userId: string;
      username: string;
    }) => {
      await apiRequest('PATCH', `/api/users/${userId}/username`, { username });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({ title: 'Success', description: 'Username updated successfully' });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update username',
        variant: 'destructive',
      });
    },
  });

  // Reset password mutation (admin only)
  const resetPasswordMutation = useMutation({
    mutationFn: async ({
      userId,
      newPassword,
    }: {
      userId: string;
      newPassword: string;
    }) => {
      await apiRequest('PATCH', `/api/users/${userId}/password`, {
        newPassword,
      });
    },
    onSuccess: () => {
      setIsCredsModalOpen(false);
      setNewPassword('');
      setConfirmNewPassword('');
      toast({ title: 'Success', description: 'Password updated successfully' });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to reset password',
        variant: 'destructive',
      });
    },
  });

  // Bulk CSV state and helpers
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState<string>(
    `firstName,lastName,email,password,role,teamId,allowTeamView\nJane,Doe,jane@example.com,Passw0rd,team_member,none,true`
  );
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkFileName, setBulkFileName] = useState<string>('');
  const [bulkTotal, setBulkTotal] = useState<number>(0);
  const [bulkDone, setBulkDone] = useState<number>(0);

  async function bulkCreateFromCSV() {
    setBulkSubmitting(true);
    try {
      // Parse CSV (simple split, assumes no commas in values)
      const lines = bulkText.split(/\r?\n/).filter(l => l.trim().length > 0);
      if (lines.length < 2)
        throw new Error('CSV must include header and at least one row');
      const header = (lines[0] || '').split(',').map(h => h.trim());
      const idx = (name: string) => header.indexOf(name);
      const out: { ok: number; fail: { row: number; reason: string }[] } = {
        ok: 0,
        fail: [],
      };

      setBulkTotal(lines.length - 1);
      setBulkDone(0);

      for (let i = 1; i < lines.length; i++) {
        const cols = (lines[i] || '').split(',');
        const payload: any = {
          firstName: cols[idx('firstName')]?.trim() || null,
          lastName: cols[idx('lastName')]?.trim() || null,
          email: cols[idx('email')]?.trim() || null,
          password: cols[idx('password')]?.trim() || '',
          role: isManager
            ? 'team_member'
            : cols[idx('role')]?.trim() || 'team_member',
        };
        const allowTeamViewVal = (cols[idx('allowTeamView')] || '')
          .trim()
          .toLowerCase();
        payload.allowTeamView =
          allowTeamViewVal === 'true' || allowTeamViewVal === '1';
        const incomingTeamId = (cols[idx('teamId')] || '').trim();
        if (incomingTeamId && incomingTeamId !== 'none')
          payload.teamId = incomingTeamId;
        // Manager hard-lock to own team
        if (isManager && managedTeam?.id) payload.teamId = managedTeam.id;
        try {
          await apiRequest('POST', '/api/users', payload);
          out.ok++;
        } catch (e: any) {
          out.fail.push({ row: i + 1, reason: e?.message || 'unknown error' });
        }
        setBulkDone(i);
      }
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: 'Bulk import finished',
        description: `${out.ok} created, ${out.fail.length} failed${out.fail.length ? ' (check console)' : ''}`,
      });
      if (out.fail.length) console.warn('Bulk user import failures:', out.fail);
      setIsBulkOpen(false);
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e?.message || 'Failed to import CSV',
        variant: 'destructive',
      });
    } finally {
      setBulkSubmitting(false);
    }
  }

  // Handler functions
  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    const teamAny = team as any;
    const lead = splitTeamLeadIdsFromTeam(teamAny, users);
    setTeamForm({
      name: team.name,
      description: team.description || '',
      managerId: teamAny.managerId || '',
      pmId: teamAny.pmId || '',
      cityId: teamAny.cityId || '',
      pmIds: Array.isArray(teamAny.pmIds)
        ? teamAny.pmIds
        : teamAny.pmId
          ? [teamAny.pmId]
          : [],
      gmId: teamAny.gmId || '',
      hodIds: lead.hodIds,
      hodId: lead.hodId,
      dpmIds: lead.dpmIds,
      dpmId: lead.dpmId,
    });
    setIsEditTeamModalOpen(true);
  };

  const handleDeleteTeam = (teamId: string) => {
    if (
      confirm(
        'Are you sure you want to delete this team? All team members will be unassigned.'
      )
    ) {
      deleteTeamMutation.mutate(teamId);
    }
  };

  if (authLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team management...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user || !['manager', 'admin'].includes(user.role)) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="text-center py-8">
              <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Access Restricted
              </h3>
              <p className="text-gray-500">
                This page is only available to managers and administrators.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // const isAdmin = user.role === 'admin';

  const handleCreateTeam = () => {
    if (!teamForm.name.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        variant: 'destructive',
      });
      return;
    }
    createTeamMutation.mutate(teamForm);
  };

  const handleEditTeamSubmit = () => {
    if (!teamForm.name.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        variant: 'destructive',
      });
      return;
    }
    if (editingTeam) {
      editTeamMutation.mutate({ teamId: editingTeam.id, teamData: teamForm });
    }
  };

  const handleUpdateUserRole = (role: string) => {
    if (selectedUser) {
      updateUserRoleMutation.mutate({ userId: selectedUser.id, role });
    }
  };

  // Toggle Manager View permission for a user
  const toggleAllowTeamView = useMutation({
    mutationFn: async ({
      userId,
      allow,
    }: {
      userId: string;
      allow: boolean;
    }) => {
      await apiRequest('PATCH', `/api/users/${userId}/allow-team-view`, {
        allow,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: 'Updated',
        description: 'Manager View permission updated',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to update permission',
        variant: 'destructive',
      });
    },
  });

  // City CRUD mutations
  const createCityMutation = useMutation({
    mutationFn: async (cityData: typeof cityForm) => {
      await apiRequest('POST', '/api/cities', cityData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cities'] });
      setIsCreateCityOpen(false);
      setCityForm({ name: '', description: '', gmId: '' });
      toast({
        title: 'Success',
        description: 'City created successfully',
      });
    },
    onError: error => {
      toast({
        title: 'Error',
        description: 'Failed to create city',
        variant: 'destructive',
      });
    },
  });

  const updateCityMutation = useMutation({
    mutationFn: async () => {
      if (!editingCity) return;
      await apiRequest('PATCH', `/api/cities/${editingCity.id}`, cityForm);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cities'] });
      setIsEditCityOpen(false);
      setEditingCity(null);
      setCityForm({ name: '', description: '', gmId: '' });
      toast({
        title: 'Success',
        description: 'City updated successfully',
      });
    },
    onError: error => {
      toast({
        title: 'Error',
        description: 'Failed to update city',
        variant: 'destructive',
      });
    },
  });

  const deleteCityMutation = useMutation({
    mutationFn: async (cityId: string) => {
      await apiRequest('DELETE', `/api/cities/${cityId}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/cities'] });
      toast({
        title: 'Success',
        description: 'City deleted successfully',
      });
    },
    onError: error => {
      toast({
        title: 'Error',
        description: 'Failed to delete city',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-6">
            <h1
              className="text-3xl font-bold text-gray-900"
              data-testid="text-page-title"
            >
              Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage teams, assign roles, and organize your workforce
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 xl:gap-8">
            {/* Cities Section - Admin Only */}
            {isAdmin && (
              <Card className="mb-6 flex flex-col h-[500px]">
                <CardHeader className="flex-shrink-0">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <CardTitle className="flex items-center">
                        <Building className="w-5 h-5 mr-2" />
                        Cities
                      </CardTitle>
                      <CardDescription>
                        Manage cities and assign General Managers (GMs)
                      </CardDescription>
                    </div>
                    <Dialog
                      open={isCreateCityOpen}
                      onOpenChange={setIsCreateCityOpen}
                    >
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <UserPlus className="w-4 h-4 mr-2" />
                          New City
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create New City</DialogTitle>
                          <DialogDescription>
                            Set up a new city and assign a General Manager
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="city-name">City Name</Label>
                            <Input
                              id="city-name"
                              value={cityForm.name}
                              onChange={e =>
                                setCityForm({
                                  ...cityForm,
                                  name: e.target.value,
                                })
                              }
                              placeholder="Enter city name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="city-description">
                              Description
                            </Label>
                            <Input
                              id="city-description"
                              value={cityForm.description}
                              onChange={e =>
                                setCityForm({
                                  ...cityForm,
                                  description: e.target.value,
                                })
                              }
                              placeholder="City description (optional)"
                            />
                          </div>
                          <div>
                            <Label htmlFor="city-gm">
                              General Manager (GM)
                            </Label>
                            <Select
                              value={cityForm.gmId}
                              onValueChange={value =>
                                setCityForm({ ...cityForm, gmId: value })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a GM (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                {users
                                  .filter(
                                    u => u.role === 'gm' || u.role === 'admin'
                                  )
                                  .sort((a, b) => {
                                    const nameA = `${a.firstName} ${a.lastName}`;
                                    const nameB = `${b.firstName} ${b.lastName}`;
                                    return nameA.localeCompare(nameB);
                                  })
                                  .map(gm => (
                                    <SelectItem key={gm.id} value={gm.id}>
                                      {gm.firstName} {gm.lastName} (
                                      {gm.role.toUpperCase()})
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            onClick={() => setIsCreateCityOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => createCityMutation.mutate(cityForm)}
                            disabled={
                              createCityMutation.isPending || !cityForm.name
                            }
                          >
                            {createCityMutation.isPending
                              ? 'Creating...'
                              : 'Create City'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto pr-2">
                  {citiesLoading ? (
                    <div>Loading cities...</div>
                  ) : cities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No cities yet. Create one to get started.
                    </div>
                  ) : (
                    <>
                      <div className="mb-4 sticky top-0 z-10 bg-white ">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input
                            placeholder="Search cities..."
                            value={citiesSearch}
                            onChange={e => setCitiesSearch(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        {cities
                          .filter(city => {
                            if (!citiesSearch.trim()) return true;
                            const searchLower = citiesSearch.toLowerCase();
                            return (
                              city.name?.toLowerCase().includes(searchLower) ||
                              city.description
                                ?.toLowerCase()
                                .includes(searchLower) ||
                              city.gm?.firstName
                                ?.toLowerCase()
                                .includes(searchLower) ||
                              city.gm?.lastName
                                ?.toLowerCase()
                                .includes(searchLower)
                            );
                          })
                          .map((city, index) => (
                            <div
                              key={city.id}
                              className="flex items-center justify-between p-4 border rounded-lg"
                            >
                              <div className="flex items-start gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-medium">
                                  {index + 1}
                                </span>
                                <div>
                                  <h3 className="font-semibold">{city.name}</h3>
                                  {city.description && (
                                    <p className="text-sm text-gray-600">
                                      {city.description}
                                    </p>
                                  )}
                                  {city.gm && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      GM: {city.gm.firstName} {city.gm.lastName}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingCity(city);
                                    setCityForm({
                                      name: city.name,
                                      description: city.description || '',
                                      gmId: city.gmId || '',
                                    });
                                    setIsEditCityOpen(true);
                                  }}
                                >
                                  Edit
                                </Button>
                                {/* <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (
                                !confirm(
                                  'Delete this city? This will unlink all users and teams.'
                                )
                              )
                                return;
                              deleteCityMutation.mutate(city.id);
                            }}
                          >
                            Delete
                          </Button> */}
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Edit City Modal */}
            <Dialog open={isEditCityOpen} onOpenChange={setIsEditCityOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit City</DialogTitle>
                  <DialogDescription>
                    Update city details and GM assignment
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="edit-city-name">City Name</Label>
                    <Input
                      id="edit-city-name"
                      value={cityForm.name}
                      onChange={e =>
                        setCityForm({ ...cityForm, name: e.target.value })
                      }
                      placeholder="Enter city name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-city-description">Description</Label>
                    <Input
                      id="edit-city-description"
                      value={cityForm.description}
                      onChange={e =>
                        setCityForm({
                          ...cityForm,
                          description: e.target.value,
                        })
                      }
                      placeholder="City description (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-city-gm">General Manager (GM)</Label>
                    <Select
                      value={cityForm.gmId}
                      onValueChange={value =>
                        setCityForm({ ...cityForm, gmId: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a GM (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {users
                          .filter(u => u.role === 'gm' || u.role === 'admin')
                          .sort((a, b) => {
                            const nameA = `${a.firstName} ${a.lastName}`;
                            const nameB = `${b.firstName} ${b.lastName}`;
                            return nameA.localeCompare(nameB);
                          })
                          .map(gm => (
                            <SelectItem key={gm.id} value={gm.id}>
                              {gm.firstName} {gm.lastName} (
                              {gm.role.toUpperCase()})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditCityOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => updateCityMutation.mutate()}
                    disabled={updateCityMutation.isPending || !cityForm.name}
                  >
                    {updateCityMutation.isPending
                      ? 'Updating...'
                      : 'Update City'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Team Projects Section */}
            <Card className="flex flex-col h-[500px]">
              <CardHeader className="flex-shrink-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Projects
                    </CardTitle>
                    <CardDescription>
                      Manage team projects and assign Project Managers (PMs)
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                    {/* City filter */}
                    {isAdmin && (
                      <div className="w-full sm:w-56">
                        <Select
                          value={projectsCityFilter}
                          onValueChange={setProjectsCityFilter}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Filter by city" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All cities</SelectItem>
                            {cities.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Dialog
                      open={isCreateTeamModalOpen}
                      onOpenChange={setIsCreateTeamModalOpen}
                    >
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="button-create-team">
                          <UserPlus className="w-4 h-4 mr-2" />
                          New Team Project
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[90vh] flex flex-col">
                        <DialogHeader>
                          <DialogTitle>Create New Team Project</DialogTitle>
                          <DialogDescription>
                            Set up a new team Project and assign a Project
                            Manager (PM)
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                          <div>
                            <Label htmlFor="team-name">Project Name</Label>
                            <Input
                              id="team-name"
                              value={teamForm.name}
                              onChange={e =>
                                setTeamForm({
                                  ...teamForm,
                                  name: e.target.value,
                                })
                              }
                              placeholder="Enter Project name"
                              data-testid="input-team-name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="team-description">
                              Description
                            </Label>
                            <Input
                              id="team-description"
                              value={teamForm.description}
                              onChange={e =>
                                setTeamForm({
                                  ...teamForm,
                                  description: e.target.value,
                                })
                              }
                              placeholder="Project description (optional)"
                              data-testid="input-team-description"
                            />
                          </div>
                          <div>
                            <Label htmlFor="team-city">City</Label>
                            <Select
                              value={teamForm.cityId}
                              onValueChange={value => {
                                // Clear PMs when city changes
                                setTeamForm({
                                  ...teamForm,
                                  cityId: value,
                                  pmIds: [],
                                  pmId: '',
                                  managerId: '',
                                  gmId: '',
                                  hodIds: [],
                                  hodId: '',
                                  dpmIds: [],
                                  dpmId: '',
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select a city (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                {cities
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(city => (
                                    <SelectItem key={city.id} value={city.id}>
                                      {city.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="team-pms">
                              Project Managers (PM) - Select Multiple
                              {!teamForm.cityId && (
                                <span className="text-gray-500 text-xs ml-2">
                                  (Select city first)
                                </span>
                              )}
                            </Label>
                            <div className="border rounded-md p-3 max-h-48 overflow-auto">
                              {!teamForm.cityId ? (
                                <p className="text-sm text-gray-500 italic">
                                  Please select a city above to see available
                                  Project Managers
                                </p>
                              ) : (
                                (() => {
                                  // Filter PMs: show PMs that have this city in their cityIds array OR have this city as their cityId
                                  const availablePMs = users
                                    .filter(u => u.role === 'pm')
                                    .filter(u => {
                                      // Check if PM has this city in their cityIds array (for PMs with multiple cities)
                                      const pmCityIds = Array.isArray(
                                        (u as any).cityIds
                                      )
                                        ? (u as any).cityIds
                                        : u.cityId
                                          ? [u.cityId]
                                          : [];
                                      return pmCityIds.includes(
                                        teamForm.cityId
                                      );
                                    })
                                    .sort((a, b) => {
                                      const nameA = `${a.firstName} ${a.lastName}`;
                                      const nameB = `${b.firstName} ${b.lastName}`;
                                      return nameA.localeCompare(nameB);
                                    });

                                  if (availablePMs.length === 0) {
                                    return (
                                      <p className="text-sm text-gray-500">
                                        No PMs available for the selected city
                                      </p>
                                    );
                                  }

                                  return availablePMs.map(pm => {
                                    const pmCityIds = Array.isArray(
                                      (pm as any).cityIds
                                    )
                                      ? (pm as any).cityIds
                                      : pm.cityId
                                        ? [pm.cityId]
                                        : [];
                                    const pmCityNames = pmCityIds
                                      .map(
                                        (id: string) =>
                                          cities.find(c => c.id === id)?.name
                                      )
                                      .filter(Boolean)
                                      .join(', ');

                                    return (
                                      <div
                                        key={pm.id}
                                        className="flex items-center space-x-2 py-1"
                                      >
                                        <input
                                          type="checkbox"
                                          id={`pm-${pm.id}`}
                                          checked={teamForm.pmIds.includes(
                                            pm.id
                                          )}
                                          onChange={e => {
                                            if (e.target.checked) {
                                              setTeamForm({
                                                ...teamForm,
                                                pmIds: [
                                                  ...teamForm.pmIds,
                                                  pm.id,
                                                ],
                                                pmId: pm.id, // Keep for backward compatibility
                                                managerId: pm.id,
                                              });
                                            } else {
                                              setTeamForm({
                                                ...teamForm,
                                                pmIds: teamForm.pmIds.filter(
                                                  id => id !== pm.id
                                                ),
                                                pmId:
                                                  teamForm.pmIds.length === 1
                                                    ? ''
                                                    : teamForm.pmId,
                                                managerId:
                                                  teamForm.pmIds.length === 1
                                                    ? ''
                                                    : teamForm.managerId,
                                              });
                                            }
                                          }}
                                          className="h-4 w-4"
                                        />
                                        <label
                                          htmlFor={`pm-${pm.id}`}
                                          className="text-sm cursor-pointer flex-1"
                                        >
                                          {pm.firstName} {pm.lastName} (PM)
                                          {pmCityNames && (
                                            <span className="text-gray-500 ml-1">
                                              - {pmCityNames}
                                            </span>
                                          )}
                                        </label>
                                      </div>
                                    );
                                  });
                                })()
                              )}
                            </div>
                          </div>

                          {/* GM Selection Section */}
                          {teamForm.cityId ? (
                            <div>
                              <Label htmlFor="team-gm">
                                General Manager (GM) - Include in Project
                              </Label>
                              <div className="border rounded-md p-3 max-h-48 overflow-auto">
                                {(() => {
                                  // Filter GMs: show GMs that have this city as their cityId
                                  const availableGMs = users
                                    .filter(u => u.role === 'gm')
                                    .filter(u => u.cityId === teamForm.cityId)
                                    .sort((a, b) => {
                                      const nameA = `${a.firstName} ${a.lastName}`;
                                      const nameB = `${b.firstName} ${b.lastName}`;
                                      return nameA.localeCompare(nameB);
                                    });

                                  if (availableGMs.length === 0) {
                                    return (
                                      <p className="text-sm text-gray-500">
                                        No GMs available for the selected city
                                      </p>
                                    );
                                  }

                                  return availableGMs.map(gm => (
                                    <div
                                      key={gm.id}
                                      className="flex items-center space-x-2 py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        id={`gm-${gm.id}`}
                                        checked={teamForm.gmId === gm.id}
                                        onChange={e => {
                                          if (e.target.checked) {
                                            setTeamForm({
                                              ...teamForm,
                                              gmId: gm.id,
                                            });
                                          } else {
                                            setTeamForm({
                                              ...teamForm,
                                              gmId: '',
                                            });
                                          }
                                        }}
                                        className="h-4 w-4"
                                      />
                                      <label
                                        htmlFor={`gm-${gm.id}`}
                                        className="text-sm cursor-pointer flex-1"
                                      >
                                        {gm.firstName} {gm.lastName} (GM)
                                        {gm.cityId && (
                                          <span className="text-gray-500 ml-1">
                                            -{' '}
                                            {cities.find(
                                              c => c.id === gm.cityId
                                            )?.name || ''}
                                          </span>
                                        )}
                                      </label>
                                    </div>
                                  ));
                                })()}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              Please select a city above to see available
                              General Managers
                            </p>
                          )}

                          {/* HOD Selection Section */}
                          {teamForm.cityId ? (
                            <>
                              <div>
                                <Label htmlFor="team-hod">
                                  Head of Department (HOD) - Include in Project
                                </Label>
                                <div className="border rounded-md p-3 max-h-48 overflow-auto">
                                  {(() => {
                                    const usersInCity = (role: 'hod' | 'dpm') =>
                                      users
                                        .filter(u => u.role === role)
                                        .filter(u => {
                                          const hodCityIds = Array.isArray(
                                            (u as any).cityIds
                                          )
                                            ? (u as any).cityIds
                                            : u.cityId
                                              ? [u.cityId]
                                              : [];
                                          return hodCityIds.includes(
                                            teamForm.cityId
                                          );
                                        })
                                        .sort((a, b) => {
                                          const nameA = `${a.firstName} ${a.lastName}`;
                                          const nameB = `${b.firstName} ${b.lastName}`;
                                          return nameA.localeCompare(nameB);
                                        });

                                    const availableHodOnly = usersInCity('hod');

                                    if (availableHodOnly.length === 0) {
                                      return (
                                        <p className="text-sm text-gray-500">
                                          No HODs available for the selected
                                          city
                                        </p>
                                      );
                                    }

                                    const allHodSelected =
                                      availableHodOnly.length > 0 &&
                                      availableHodOnly.every(h =>
                                        teamForm.hodIds.includes(h.id)
                                      );

                                    return (
                                      <>
                                        <div className="flex items-center space-x-2 py-1 border-b mb-2 pb-2">
                                          <input
                                            type="checkbox"
                                            id="hod-select-all"
                                            checked={allHodSelected}
                                            onChange={e => {
                                              if (e.target.checked) {
                                                const merged = [
                                                  ...teamForm.hodIds.filter(
                                                    id =>
                                                      !availableHodOnly.some(
                                                        h => h.id === id
                                                      )
                                                  ),
                                                  ...availableHodOnly.map(
                                                    h => h.id
                                                  ),
                                                ];
                                                setTeamForm({
                                                  ...teamForm,
                                                  hodIds: merged,
                                                  hodId: merged[0] || '',
                                                });
                                              } else {
                                                const keep =
                                                  teamForm.hodIds.filter(
                                                    id =>
                                                      !availableHodOnly.some(
                                                        h => h.id === id
                                                      )
                                                  );
                                                setTeamForm({
                                                  ...teamForm,
                                                  hodIds: keep,
                                                  hodId: keep[0] || '',
                                                });
                                              }
                                            }}
                                            className="h-4 w-4"
                                          />
                                          <label
                                            htmlFor="hod-select-all"
                                            className="text-sm cursor-pointer flex-1 font-medium"
                                          >
                                            Select All (HOD)
                                          </label>
                                        </div>
                                        {availableHodOnly.map(hod => {
                                          const hodCityIds = Array.isArray(
                                            (hod as any).cityIds
                                          )
                                            ? (hod as any).cityIds
                                            : hod.cityId
                                              ? [hod.cityId]
                                              : [];
                                          const hodCityNames = hodCityIds
                                            .map(
                                              (id: string) =>
                                                cities.find(c => c.id === id)
                                                  ?.name
                                            )
                                            .filter(Boolean)
                                            .join(', ');

                                          return (
                                            <div
                                              key={hod.id}
                                              className="flex items-center space-x-2 py-1"
                                            >
                                              <input
                                                type="checkbox"
                                                id={`hod-${hod.id}`}
                                                checked={teamForm.hodIds.includes(
                                                  hod.id
                                                )}
                                                onChange={e => {
                                                  if (e.target.checked) {
                                                    setTeamForm({
                                                      ...teamForm,
                                                      hodIds: [
                                                        ...teamForm.hodIds,
                                                        hod.id,
                                                      ],
                                                      hodId:
                                                        teamForm.hodIds
                                                          .length === 0
                                                          ? hod.id
                                                          : teamForm.hodId,
                                                    });
                                                  } else {
                                                    setTeamForm({
                                                      ...teamForm,
                                                      hodIds:
                                                        teamForm.hodIds.filter(
                                                          id => id !== hod.id
                                                        ),
                                                      hodId:
                                                        teamForm.hodIds
                                                          .length === 1
                                                          ? ''
                                                          : teamForm.hodId,
                                                    });
                                                  }
                                                }}
                                                className="h-4 w-4"
                                              />
                                              <label
                                                htmlFor={`hod-${hod.id}`}
                                                className="text-sm cursor-pointer flex-1"
                                              >
                                                {hod.firstName} {hod.lastName}{' '}
                                                (HOD)
                                                {hodCityNames && (
                                                  <span className="text-gray-500 ml-1">
                                                    - {hodCityNames}
                                                  </span>
                                                )}
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* DPM Selection — stored on team.dpm_ids */}
                              <div>
                                <Label htmlFor="team-dpm">
                                  Deputy Project Manager (DPM) - Include in
                                  Project
                                </Label>
                                <div className="border rounded-md p-3 max-h-48 overflow-auto">
                                  {(() => {
                                    const usersInCity = (role: 'hod' | 'dpm') =>
                                      users
                                        .filter(u => u.role === role)
                                        .filter(u => {
                                          const ids = Array.isArray(
                                            (u as any).cityIds
                                          )
                                            ? (u as any).cityIds
                                            : u.cityId
                                              ? [u.cityId]
                                              : [];
                                          return ids.includes(teamForm.cityId);
                                        })
                                        .sort((a, b) => {
                                          const nameA = `${a.firstName} ${a.lastName}`;
                                          const nameB = `${b.firstName} ${b.lastName}`;
                                          return nameA.localeCompare(nameB);
                                        });

                                    const availableDpmOnly = usersInCity('dpm');

                                    if (availableDpmOnly.length === 0) {
                                      return (
                                        <p className="text-sm text-gray-500">
                                          No DPMs available for the selected
                                          city
                                        </p>
                                      );
                                    }

                                    const allDpmSelected =
                                      availableDpmOnly.length > 0 &&
                                      availableDpmOnly.every(d =>
                                        teamForm.dpmIds.includes(d.id)
                                      );

                                    return (
                                      <>
                                        <div className="flex items-center space-x-2 py-1 border-b mb-2 pb-2">
                                          <input
                                            type="checkbox"
                                            id="dpm-select-all"
                                            checked={allDpmSelected}
                                            onChange={e => {
                                              if (e.target.checked) {
                                                const merged = [
                                                  ...teamForm.dpmIds.filter(
                                                    id =>
                                                      !availableDpmOnly.some(
                                                        d => d.id === id
                                                      )
                                                  ),
                                                  ...availableDpmOnly.map(
                                                    d => d.id
                                                  ),
                                                ];
                                                setTeamForm({
                                                  ...teamForm,
                                                  dpmIds: merged,
                                                  dpmId: merged[0] || '',
                                                });
                                              } else {
                                                const keep =
                                                  teamForm.dpmIds.filter(
                                                    id =>
                                                      !availableDpmOnly.some(
                                                        d => d.id === id
                                                      )
                                                  );
                                                setTeamForm({
                                                  ...teamForm,
                                                  dpmIds: keep,
                                                  dpmId: keep[0] || '',
                                                });
                                              }
                                            }}
                                            className="h-4 w-4"
                                          />
                                          <label
                                            htmlFor="dpm-select-all"
                                            className="text-sm cursor-pointer flex-1 font-medium"
                                          >
                                            Select All (DPM)
                                          </label>
                                        </div>
                                        {availableDpmOnly.map(dpm => {
                                          const dpmCityIds = Array.isArray(
                                            (dpm as any).cityIds
                                          )
                                            ? (dpm as any).cityIds
                                            : dpm.cityId
                                              ? [dpm.cityId]
                                              : [];
                                          const dpmCityNames = dpmCityIds
                                            .map(
                                              (id: string) =>
                                                cities.find(c => c.id === id)
                                                  ?.name
                                            )
                                            .filter(Boolean)
                                            .join(', ');

                                          return (
                                            <div
                                              key={dpm.id}
                                              className="flex items-center space-x-2 py-1"
                                            >
                                              <input
                                                type="checkbox"
                                                id={`dpm-${dpm.id}`}
                                                checked={teamForm.dpmIds.includes(
                                                  dpm.id
                                                )}
                                                onChange={e => {
                                                  if (e.target.checked) {
                                                    setTeamForm({
                                                      ...teamForm,
                                                      dpmIds: [
                                                        ...teamForm.dpmIds,
                                                        dpm.id,
                                                      ],
                                                      dpmId:
                                                        teamForm.dpmIds
                                                          .length === 0
                                                          ? dpm.id
                                                          : teamForm.dpmId,
                                                    });
                                                  } else {
                                                    setTeamForm({
                                                      ...teamForm,
                                                      dpmIds:
                                                        teamForm.dpmIds.filter(
                                                          id => id !== dpm.id
                                                        ),
                                                      dpmId:
                                                        teamForm.dpmIds
                                                          .length === 1
                                                          ? ''
                                                          : teamForm.dpmId,
                                                    });
                                                  }
                                                }}
                                                className="h-4 w-4"
                                              />
                                              <label
                                                htmlFor={`dpm-${dpm.id}`}
                                                className="text-sm cursor-pointer flex-1"
                                              >
                                                {dpm.firstName} {dpm.lastName}{' '}
                                                (DPM)
                                                {dpmCityNames && (
                                                  <span className="text-gray-500 ml-1">
                                                    - {dpmCityNames}
                                                  </span>
                                                )}
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            </>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              Please select a city above to see available HOD
                              and DPM options
                            </p>
                          )}
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            onClick={() => setIsCreateTeamModalOpen(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleCreateTeam}
                            disabled={createTeamMutation.isPending}
                            data-testid="button-submit-team"
                          >
                            {createTeamMutation.isPending
                              ? 'Creating...'
                              : 'Create Team'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto pr-2">
                {teamsLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : teams.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No teams created yet</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 sticky top-0 z-10 bg-white">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search projects..."
                          value={projectsSearch}
                          onChange={e => setProjectsSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      {teams
                        .filter(team => {
                          if (!projectsSearch.trim()) return true;
                          const searchLower = projectsSearch.toLowerCase();
                          return (
                            team.name?.toLowerCase().includes(searchLower) ||
                            team.description
                              ?.toLowerCase()
                              .includes(searchLower)
                          );
                        })
                        // Apply city filter
                        .filter(team => {
                          if (projectsCityFilter === 'all') return true;
                          const teamAny = team as any;
                          return teamAny.cityId === projectsCityFilter;
                        })
                        .map((team, index) => (
                          <div
                            key={team.id}
                            className="p-3 border border-gray-200 rounded-lg"
                            data-testid={`team-card-${team.id}`}
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div className="flex flex-1 gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-medium">
                                  {index + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900 mb-1">
                                    {team.name}
                                  </h4>
                                  {team.description && (
                                    <p className="text-sm text-gray-600 mb-2">
                                      {team.description}
                                    </p>
                                  )}
                                  <div className="flex items-center text-xs text-gray-500">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    Created{' '}
                                    {new Date(
                                      team.createdAt!
                                    ).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              {user?.role === 'admin' && (
                                <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditTeam(team)}
                                    data-testid={`button-edit-team-${team.id}`}
                                  >
                                    <Settings className="w-4 h-4 mr-1" />
                                    Edit
                                  </Button>
                                  {/* <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteTeam(team.id)}
                                disabled={deleteTeamMutation.isPending}
                                data-testid={`button-delete-team-${team.id}`}
                              >
                                {deleteTeamMutation.isPending
                                  ? 'Deleting...'
                                  : 'Delete'}
                              </Button> */}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Users Section */}
            <Card className="flex flex-col h-[500px] col-span-1 lg:col-span-2">
              <CardHeader className="flex-shrink-0">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      PM/GM/HOD/DPM/STAFF
                    </CardTitle>
                    <CardDescription>
                      Manage user roles and permissions
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
                    {/* Bulk Add CSV */}
                    {(isAdmin || isManager) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsBulkOpen(true)}
                      >
                        Bulk Add (CSV)
                      </Button>
                    )}
                    {/* City filter */}
                    {isAdmin && (
                      <div className="w-full sm:w-56">
                        <Select
                          value={membersCityFilter}
                          onValueChange={setMembersCityFilter}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Filter by city" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All cities</SelectItem>
                            {cities.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {/* Team filter */}
                    {/* <div className="w-full sm:w-56">
                      <Select
                        value={membersTeamFilter}
                        onValueChange={setMembersTeamFilter}
                        disabled={isManager && !!managedTeam}
                      >
                        <SelectTrigger
                          className="w-full"
                          data-testid="select-members-team-filter"
                        >
                          <SelectValue placeholder="Filter by team" />
                        </SelectTrigger>
                        <SelectContent>
                          {isAdmin && (
                            <SelectItem value="all">All Projects</SelectItem>
                          )}
                          {isAdmin && (
                            <SelectItem value="none">No Projects</SelectItem>
                          )}
                          {isManager && myTeamId && (
                            <SelectItem value={myTeamId}>
                              My Team (
                              {teams.find(t => t.id === myTeamId)?.name ||
                                'My Team'}
                              )
                            </SelectItem>
                          )}
                          {isAdmin &&
                            teams.map(t => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div> */}
                    {(isAdmin || isManager) && (
                      <Button
                        size="sm"
                        onClick={() => {
                          // For managers, lock teamId to managed team
                          if (isManager && myTeamId) {
                            setMemberForm(prev => ({
                              ...prev,
                              teamId: myTeamId!,
                            }));
                          }
                          setIsCreateMemberOpen(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto pr-2">
                {usersLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No team members found</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 sticky top-0 z-10 bg-white ">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search team members..."
                          value={membersSearch}
                          onChange={e => setMembersSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      {users
                        // Scope by role
                        .filter(
                          member =>
                            isAdmin ||
                            (isManager &&
                              (member.id === user.id ||
                                member.teamId === managedTeam?.id ||
                                ((member as any).teams || []).some(
                                  (t: any) => t.id === managedTeam?.id
                                )))
                        )
                        // Apply team filter (per specific team project)
                        .filter(member => {
                          if (membersTeamFilter === 'all') return true;
                          if (membersTeamFilter === 'none')
                            return (
                              !member.teamId &&
                              !((member as any).teams || []).length
                            );

                          // Direct team/project membership (primary or extra team)
                          const inTeamDirect =
                            member.teamId === membersTeamFilter ||
                            ((member as any).teams || []).some(
                              (t: any) => t.id === membersTeamFilter
                            );
                          if (inTeamDirect) return true;

                          // Also include PMs assigned to this specific team project (support multiple PMs)
                          const selectedTeam = teams.find(
                            t => t.id === membersTeamFilter
                          ) as any;
                          if (selectedTeam) {
                            // Check single pmId (backward compatibility)
                            if (member.id === selectedTeam.pmId) {
                              return true;
                            }
                            // Check multiple pmIds array
                            if (
                              Array.isArray(selectedTeam.pmIds) &&
                              selectedTeam.pmIds.includes(member.id)
                            ) {
                              return true;
                            }
                          }

                          return false;
                        })
                        // Apply city filter
                        .filter(member => {
                          if (membersCityFilter === 'all') return true;
                          return (member as any).cityId === membersCityFilter;
                        })
                        // Apply search filter
                        .filter(member => {
                          if (!membersSearch.trim()) return true;
                          const searchLower = membersSearch.toLowerCase();
                          const fullName =
                            `${member.firstName} ${member.lastName}`.toLowerCase();
                          const email = member.email?.toLowerCase() || '';
                          const username = (
                            (member as any).username || ''
                          ).toLowerCase();
                          const role = member.role?.toLowerCase() || '';
                          return (
                            fullName.includes(searchLower) ||
                            email.includes(searchLower) ||
                            username.includes(searchLower) ||
                            role.includes(searchLower)
                          );
                        })
                        .map((member, index) => (
                          <div
                            key={member.id}
                            className="p-3 border border-gray-200 rounded-lg"
                            data-testid={`user-card-${member.id}`}
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div className="flex flex-1 gap-3">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-medium">
                                  {index + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center mb-2">
                                    <h4 className="font-medium text-gray-900 mr-2">
                                      {member.firstName} {member.lastName}
                                    </h4>
                                    <Badge
                                      variant={
                                        member.role === 'admin'
                                          ? 'destructive'
                                          : member.role === 'pm' ||
                                              member.role === 'gm' ||
                                              member.role === 'hod' ||
                                              member.role === 'dpm' ||
                                              member.role === 'staff'
                                            ? 'default'
                                            : 'secondary'
                                      }
                                    >
                                      {member.role === 'pm'
                                        ? 'PM'
                                        : member.role === 'gm'
                                          ? 'GM'
                                          : member.role === 'hod'
                                            ? 'HOD'
                                            : member.role === 'dpm'
                                              ? 'DPM'
                                              : member.role === 'staff'
                                                ? 'Staff'
                                                : member.role.replace('_', ' ')}
                                    </Badge>
                                    {isAdmin &&
                                      (member as any).isActive === false && (
                                        <Badge
                                          variant="outline"
                                          className="ml-1 border-amber-600 text-amber-800"
                                        >
                                          Inactive
                                        </Badge>
                                      )}
                                  </div>

                                  {member.email && (
                                    <div className="flex items-center text-sm text-gray-600 mb-1">
                                      <Mail className="w-3 h-3 mr-1" />
                                      {member.email}
                                    </div>
                                  )}
                                  {(member as any).username && (
                                    <div className="flex items-center text-sm text-gray-600 mb-1">
                                      <Shield className="w-3 h-3 mr-1" />
                                      Username: {(member as any).username}
                                    </div>
                                  )}

                                  <div className="flex items-start text-sm text-gray-600">
                                    {/* <Building className="w-3 h-3 mr-1 mt-0.5" /> */}
                                    <div>
                                      {member.role === 'gm' ? (
                                        <div>
                                          City:{' '}
                                          {cities.find(
                                            c => c.id === (member as any).cityId
                                          )?.name || 'No city assigned'}
                                        </div>
                                      ) : member.role === 'pm' ||
                                        member.role === 'hod' ||
                                        member.role === 'dpm' ? (
                                        <div>
                                          Cities:{' '}
                                          {(() => {
                                            const pmCityIds = Array.isArray(
                                              (member as any).cityIds
                                            )
                                              ? (member as any).cityIds
                                              : (member as any).cityId
                                                ? [(member as any).cityId]
                                                : [];
                                            const cityNames = pmCityIds
                                              .map(
                                                (id: string) =>
                                                  cities.find(c => c.id === id)
                                                    ?.name
                                              )
                                              .filter(Boolean);
                                            return cityNames.length > 0
                                              ? cityNames.join(', ')
                                              : 'No cities assigned';
                                          })()}
                                        </div>
                                      ) : (
                                        <>
                                          {/* <div>
                                            Primary:{' '}
                                            {member.team?.name ||
                                              'No team assigned'}
                                          </div> */}
                                          {/* {(member as any).teams?.length ? (
                                            <div className="text-xs text-gray-500">
                                              Also in:{' '}
                                              {(member as any).teams
                                                .map((t: any) => t.name)
                                                .join(', ')}
                                            </div>
                                          ) : null} */}
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Allow Manager View toggle (for team members only) */}
                                  {member.role === 'team_member' &&
                                    (isAdmin ||
                                      (isManager &&
                                        (member.teamId === myTeamId ||
                                          ((member as any).teams || []).some(
                                            (t: any) => t.id === myTeamId
                                          )))) && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <label className="text-xs text-gray-600">
                                          Allow Manager View
                                        </label>
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4"
                                          checked={
                                            !!(member as any).allowTeamView
                                          }
                                          onChange={e =>
                                            toggleAllowTeamView.mutate({
                                              userId: member.id,
                                              allow: e.target.checked,
                                            })
                                          }
                                        />
                                      </div>
                                    )}

                                  {(isAdmin ||
                                    (isManager &&
                                      member.id !== user.id &&
                                      (member.teamId === myTeamId ||
                                        ((member as any).teams || []).some(
                                          (t: any) => t.id === myTeamId
                                        )))) &&
                                    // Don't show Manage Cities/Projects for admin or staff - staff have no city assignment
                                    member.role !== 'admin' &&
                                    member.role !== 'staff' && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="ml-2"
                                          onClick={async () => {
                                            // Refetch latest user data to ensure we have updated cityIds
                                            await queryClient.refetchQueries({
                                              queryKey: ['/api/users'],
                                            });
                                            // Get the latest member data from the query cache
                                            const latestUsers =
                                              queryClient.getQueryData<
                                                UserWithTeam[]
                                              >(['/api/users']) || users;
                                            const latestMember =
                                              latestUsers.find(
                                                u => u.id === member.id
                                              ) || member;

                                            setSelectedUser(latestMember);
                                            // For GM/PM: use cityId/cityIds, for team members: use teamId
                                            if (latestMember.role === 'gm') {
                                              const cityId =
                                                (latestMember as any).cityId ||
                                                'none';
                                              setPendingPrimaryTeam(cityId);
                                              setPendingExtraTeams([]);
                                              setPendingCities([]);
                                            } else if (
                                              latestMember.role === 'pm' ||
                                              latestMember.role === 'hod' ||
                                              latestMember.role === 'dpm'
                                            ) {
                                              // For PM and HOD: use cityIds array
                                              let cityIds: string[] = [];
                                              if (
                                                (latestMember as any).cityIds
                                              ) {
                                                // Handle both JSON string and array formats
                                                if (
                                                  typeof (latestMember as any)
                                                    .cityIds === 'string'
                                                ) {
                                                  try {
                                                    cityIds = JSON.parse(
                                                      (latestMember as any)
                                                        .cityIds
                                                    );
                                                  } catch (e) {
                                                    cityIds = [];
                                                  }
                                                } else if (
                                                  Array.isArray(
                                                    (latestMember as any)
                                                      .cityIds
                                                  )
                                                ) {
                                                  cityIds = (
                                                    latestMember as any
                                                  ).cityIds;
                                                }
                                              }
                                              // Fallback to cityId if cityIds is empty
                                              if (
                                                cityIds.length === 0 &&
                                                (latestMember as any).cityId
                                              ) {
                                                cityIds = [
                                                  (latestMember as any).cityId,
                                                ];
                                              }
                                              setPendingCities(cityIds);
                                              setPendingPrimaryTeam(
                                                cityIds.length > 0
                                                  ? cityIds[0]
                                                  : 'none'
                                              ); // For backward compatibility
                                              setPendingExtraTeams([]);
                                            } else {
                                              // Prefill modal state based on current selection
                                              const primary = member.team?.id
                                                ? member.team.id
                                                : 'none';
                                              setPendingPrimaryTeam(
                                                primary as any
                                              );
                                              const extras = (
                                                (member as any).teams || []
                                              ).map((t: any) => t.id);
                                              setPendingExtraTeams(extras);
                                            }
                                            setIsTeamsModalOpen(true);
                                          }}
                                        >
                                          Manage Cities
                                        </Button>
                                        {/* Manage Projects button - only for PM, GM, and HOD (not admin) */}
                                        {(member.role === 'pm' ||
                                          member.role === 'gm' ||
                                          member.role === 'hod' ||
                                          member.role === 'dpm') && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="ml-2"
                                            onClick={async () => {
                                              setSelectedUser(member);
                                              // Get user's assigned cities
                                              let userCityIds: string[] = [];
                                              if (member.role === 'gm') {
                                                if ((member as any).cityId) {
                                                  userCityIds = [
                                                    (member as any).cityId,
                                                  ];
                                                }
                                              } else if (
                                                member.role === 'pm' ||
                                                member.role === 'hod' ||
                                                member.role === 'dpm'
                                              ) {
                                                if ((member as any).cityIds) {
                                                  if (
                                                    typeof (member as any)
                                                      .cityIds === 'string'
                                                  ) {
                                                    try {
                                                      userCityIds = JSON.parse(
                                                        (member as any).cityIds
                                                      );
                                                    } catch (e) {
                                                      userCityIds = [];
                                                    }
                                                  } else if (
                                                    Array.isArray(
                                                      (member as any).cityIds
                                                    )
                                                  ) {
                                                    userCityIds = (
                                                      member as any
                                                    ).cityIds;
                                                  }
                                                }
                                                if (
                                                  userCityIds.length === 0 &&
                                                  (member as any).cityId
                                                ) {
                                                  userCityIds = [
                                                    (member as any).cityId,
                                                  ];
                                                }
                                              }

                                              // Get projects assigned to this user
                                              const userProjects: string[] = [];
                                              teams.forEach((team: any) => {
                                                if (
                                                  member.role === 'gm' &&
                                                  team.gmId === member.id
                                                ) {
                                                  userProjects.push(team.id);
                                                } else if (
                                                  member.role === 'pm'
                                                ) {
                                                  if (
                                                    team.pmId === member.id ||
                                                    (Array.isArray(
                                                      team.pmIds
                                                    ) &&
                                                      team.pmIds.includes(
                                                        member.id
                                                      ))
                                                  ) {
                                                    userProjects.push(team.id);
                                                  }
                                                } else if (
                                                  member.role === 'hod'
                                                ) {
                                                  if (
                                                    team.hodId === member.id ||
                                                    (Array.isArray(
                                                      team.hodIds
                                                    ) &&
                                                      team.hodIds.includes(
                                                        member.id
                                                      ))
                                                  ) {
                                                    userProjects.push(team.id);
                                                  }
                                                } else if (
                                                  member.role === 'dpm'
                                                ) {
                                                  if (
                                                    (team as any).dpmId ===
                                                      member.id ||
                                                    (Array.isArray(
                                                      (team as any).dpmIds
                                                    ) &&
                                                      (
                                                        team as any
                                                      ).dpmIds.includes(
                                                        member.id
                                                      )) ||
                                                    team.hodId === member.id ||
                                                    (Array.isArray(
                                                      team.hodIds
                                                    ) &&
                                                      team.hodIds.includes(
                                                        member.id
                                                      ))
                                                  ) {
                                                    userProjects.push(team.id);
                                                  }
                                                }
                                              });

                                              setPendingProjects(userProjects);
                                              setIsProjectsModalOpen(true);
                                            }}
                                          >
                                            Manage Projects
                                          </Button>
                                        )}
                                      </>
                                    )}
                                </div>
                              </div>

                              {isAdmin && member.id !== user.id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUser(member);
                                    // Pre-fill edit form with user data
                                    const userCityIds = Array.isArray(
                                      (member as any).cityIds
                                    )
                                      ? (member as any).cityIds
                                      : (member as any).cityId
                                        ? [(member as any).cityId]
                                        : [];
                                    const userCityId =
                                      (member as any).cityId || 'none';
                                    setEditUserForm({
                                      firstName: member.firstName || '',
                                      lastName: member.lastName || '',
                                      email: member.email || '',
                                      username: (member as any).username || '',
                                      password: '',
                                      confirmPassword: '',
                                      teamId: member.teamId || 'none',
                                      cityId: userCityId,
                                      cityIds: userCityIds,
                                      role: member.role as any,
                                      allowTeamView:
                                        (member as any).allowTeamView || false,
                                      assignedByPmId:
                                        (member as any).assignedByPmId ||
                                        'none',
                                      assignedByHodId:
                                        (member as any).assignedByHodId ||
                                        'none',
                                      isActive: (member as any).isActive !== false,
                                    });
                                    setIsEditUserModalOpen(true);
                                  }}
                                  data-testid={`button-edit-user-${member.id}`}
                                >
                                  <Settings className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                              )}

                              {(isAdmin ||
                                (isManager &&
                                  member.id !== user.id &&
                                  member.teamId === myTeamId)) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUser(member);
                                    setIsCredsModalOpen(true);
                                  }}
                                  className="ml-2"
                                >
                                  Credentials
                                </Button>
                              )}

                              {/* {isAdmin && member.id !== user.id && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="ml-2"
                                onClick={async () => {
                                  if (
                                    !confirm(
                                      'Delete this user? This is irreversible.'
                                    )
                                  )
                                    return;
                                  try {
                                    const res = await fetch(
                                      `/api/users/${member.id}`,
                                      {
                                        method: 'DELETE',
                                        credentials: 'include',
                                      }
                                    );
                                    const text = await res.text();
                                    if (res.ok) {
                                      queryClient.invalidateQueries({
                                        queryKey: ['/api/users'],
                                      });
                                      toast({
                                        title: 'Success',
                                        description:
                                          'User deleted successfully',
                                      });
                                    } else {
                                      toast({
                                        title: 'Cannot delete',
                                        description:
                                          text || 'User cannot be deleted',
                                        variant: 'destructive',
                                      });
                                    }
                                  } catch (_) {
                                    toast({
                                      title: 'Error',
                                      description: 'Failed to delete user',
                                      variant: 'destructive',
                                    });
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            )} */}
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Edit User Modal */}
          <Dialog
            open={isEditUserModalOpen}
            onOpenChange={open => {
              setIsEditUserModalOpen(open);
              if (!open) {
                setSelectedUser(null);
                setEditUserForm({
                  firstName: '',
                  lastName: '',
                  email: '',
                  username: '',
                  password: '',
                  confirmPassword: '',
                  teamId: 'none',
                  cityId: 'none',
                  cityIds: [],
                  role: 'hod',
                  allowTeamView: false,
                  assignedByPmId: 'none',
                  assignedByHodId: 'none',
                  isActive: true,
                });
              }
            }}
          >
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
                <DialogDescription>
                  Update user information for {selectedUser?.firstName}{' '}
                  {selectedUser?.lastName}
                </DialogDescription>
              </DialogHeader>

              {selectedUser && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={editUserForm.firstName}
                        onChange={e =>
                          setEditUserForm({
                            ...editUserForm,
                            firstName: e.target.value,
                          })
                        }
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={editUserForm.lastName}
                        onChange={e =>
                          setEditUserForm({
                            ...editUserForm,
                            lastName: e.target.value,
                          })
                        }
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={editUserForm.email}
                      onChange={e =>
                        setEditUserForm({
                          ...editUserForm,
                          email: e.target.value,
                        })
                      }
                      placeholder="email@example.com"
                    />
                  </div>
                  {isAdmin && selectedUser?.id !== user?.id && (
                    <div className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
                      <div className="space-y-1">
                        <Label>Account active</Label>
                        <p className="text-xs text-muted-foreground max-w-md">
                          Inactive users cannot sign in. Tasks where every
                          assignee is inactive are hidden from non-admins;
                          shared tasks remain visible if at least one assignee
                          is active.
                        </p>
                      </div>
                      <Switch
                        checked={editUserForm.isActive}
                        onCheckedChange={v =>
                          setEditUserForm({ ...editUserForm, isActive: v })
                        }
                      />
                    </div>
                  )}
                  <div>
                    <Label>Username</Label>
                    <Input
                      value={editUserForm.username}
                      onChange={e =>
                        setEditUserForm({
                          ...editUserForm,
                          username: e.target.value,
                        })
                      }
                      placeholder="Enter username"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Allowed: letters, numbers, dot, underscore, hyphen
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>New Password (Leave blank to keep current)</Label>
                      <Input
                        type="password"
                        value={editUserForm.password}
                        onChange={e =>
                          setEditUserForm({
                            ...editUserForm,
                            password: e.target.value,
                          })
                        }
                        placeholder="Min 6 characters"
                      />
                    </div>
                    <div>
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={editUserForm.confirmPassword}
                        onChange={e =>
                          setEditUserForm({
                            ...editUserForm,
                            confirmPassword: e.target.value,
                          })
                        }
                        placeholder="Re-enter password"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {editUserForm.role !== 'staff' ? (
                      <>
                        <div>
                          <Label>Role</Label>
                          <Select
                            value={editUserForm.role}
                            onValueChange={v =>
                              setEditUserForm({
                                ...editUserForm,
                                role: v as any,
                              })
                            }
                            disabled={isManager}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin" disabled={!isAdmin}>
                                Admin
                              </SelectItem>
                              <SelectItem value="hod" disabled={!isAdmin}>
                                HOD (Head of Department)
                              </SelectItem>
                              <SelectItem value="dpm" disabled={!isAdmin}>
                                DPM
                              </SelectItem>
                              <SelectItem value="pm" disabled={!isAdmin}>
                                PM (Project Manager)
                              </SelectItem>
                              <SelectItem value="gm" disabled={!isAdmin}>
                                GM (General Manager)
                              </SelectItem>
                              <SelectItem value="staff" disabled={!isAdmin}>
                                Staff
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          {editUserForm.role === 'admin' ? (
                            <div className="text-sm text-gray-500 py-2">
                              Admin users have full system access and don't
                              require city or team assignments.
                            </div>
                          ) : editUserForm.role === 'gm' ? (
                            <>
                              <Label>
                                City <span className="text-red-500">*</span>
                              </Label>
                              <Select
                                value={editUserForm.cityId}
                                onValueChange={v =>
                                  setEditUserForm({
                                    ...editUserForm,
                                    cityId: v as any,
                                  })
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select city" />
                                </SelectTrigger>
                                <SelectContent>
                                  {cities
                                    .sort((a, b) =>
                                      a.name.localeCompare(b.name)
                                    )
                                    .map(c => (
                                      <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              {cities.length === 0 && (
                                <p className="text-xs text-amber-600 mt-1">
                                  No cities available. Create a city first.
                                </p>
                              )}
                            </>
                          ) : editUserForm.role === 'pm' ||
                            editUserForm.role === 'hod' ||
                            editUserForm.role === 'dpm' ? (
                            <>
                              <Label>
                                Cities (Select Multiple){' '}
                                <span className="text-red-500">*</span>
                              </Label>
                              <div className="border rounded-md p-3 max-h-48 overflow-auto">
                                {cities.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    No cities available. Create a city first.
                                  </p>
                                ) : (
                                  (() => {
                                    const sortedCities = [...cities].sort(
                                      (a, b) => a.name.localeCompare(b.name)
                                    );
                                    const allCityIds = sortedCities.map(
                                      c => c.id
                                    );
                                    const allSelected =
                                      allCityIds.length > 0 &&
                                      allCityIds.every(id =>
                                        editUserForm.cityIds.includes(id)
                                      );

                                    return (
                                      <>
                                        {/* HOD-only: show Select All */}
                                        {(editUserForm.role === 'hod' ||
                                          editUserForm.role === 'dpm') && (
                                          <div className="flex items-center space-x-2 py-1 border-b mb-2 pb-2">
                                            <input
                                              type="checkbox"
                                              id="edit-city-select-all"
                                              checked={allSelected}
                                              onChange={e => {
                                                const checked =
                                                  e.target.checked;
                                                setEditUserForm({
                                                  ...editUserForm,
                                                  cityIds: checked
                                                    ? allCityIds
                                                    : [],
                                                  cityId:
                                                    checked &&
                                                    allCityIds.length > 0
                                                      ? allCityIds[0]
                                                      : 'none',
                                                });
                                              }}
                                              className="h-4 w-4"
                                            />
                                            <label
                                              htmlFor="edit-city-select-all"
                                              className="text-sm cursor-pointer flex-1 font-medium"
                                            >
                                              Select all cities
                                            </label>
                                          </div>
                                        )}

                                        {sortedCities.map(city => (
                                          <div
                                            key={city.id}
                                            className="flex items-center space-x-2 py-1"
                                          >
                                            <input
                                              type="checkbox"
                                              id={`edit-city-${city.id}`}
                                              checked={editUserForm.cityIds.includes(
                                                city.id
                                              )}
                                              onChange={e => {
                                                if (e.target.checked) {
                                                  setEditUserForm({
                                                    ...editUserForm,
                                                    cityIds: [
                                                      ...editUserForm.cityIds,
                                                      city.id,
                                                    ],
                                                    cityId: city.id, // Keep first city for backward compatibility
                                                  });
                                                } else {
                                                  const nextCityIds =
                                                    editUserForm.cityIds.filter(
                                                      id => id !== city.id
                                                    );
                                                  setEditUserForm({
                                                    ...editUserForm,
                                                    cityIds: nextCityIds,
                                                    cityId:
                                                      nextCityIds.length === 0
                                                        ? 'none'
                                                        : editUserForm.cityId,
                                                  });
                                                }
                                              }}
                                              className="h-4 w-4"
                                            />
                                            <label
                                              htmlFor={`edit-city-${city.id}`}
                                              className="text-sm cursor-pointer flex-1"
                                            >
                                              {city.name}
                                            </label>
                                          </div>
                                        ))}
                                      </>
                                    );
                                  })()
                                )}
                              </div>
                            </>
                          ) : (
                            <>
                              <Label>Primary Team</Label>
                              <Select
                                value={editUserForm.teamId}
                                onValueChange={v =>
                                  setEditUserForm({
                                    ...editUserForm,
                                    teamId: v as any,
                                  })
                                }
                                disabled={isManager && !!myTeamId}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select team" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">
                                    No primary team
                                  </SelectItem>
                                  {teams
                                    .sort((a, b) =>
                                      a.name.localeCompare(b.name)
                                    )
                                    .map(t => (
                                      <SelectItem
                                        key={t.id}
                                        value={t.id}
                                        disabled={
                                          isManager && myTeamId !== t.id
                                        }
                                      >
                                        {t.name}
                                        {isManager && myTeamId !== t.id
                                          ? ' (not allowed)'
                                          : ''}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Staff: show only Leader (under whom assigned) and City */
                      <div className="col-span-2 space-y-4">
                        <div>
                          <Label>
                            City <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={editUserForm.cityId}
                            onValueChange={v =>
                              setEditUserForm({
                                ...editUserForm,
                                cityId: v as any,
                                assignedByPmId: 'none',
                                assignedByHodId: 'none',
                              })
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select city" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select city</SelectItem>
                              {cities
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map(c => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>
                            Leader (Assign Under){' '}
                            <span className="text-red-500">*</span>
                          </Label>
                          <div className="grid grid-cols-2 gap-3 mt-1">
                            <div>
                              <Label className="text-sm font-normal text-gray-600">
                                PM
                              </Label>
                              <Select
                                value={editUserForm.assignedByPmId}
                                onValueChange={v =>
                                  setEditUserForm({
                                    ...editUserForm,
                                    assignedByPmId: v as any,
                                    assignedByHodId:
                                      v !== 'none'
                                        ? 'none'
                                        : editUserForm.assignedByHodId,
                                  })
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select PM" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No PM</SelectItem>
                                  {users
                                    .filter((u: any) => {
                                      if (u.role !== 'pm') return false;
                                      const pmCityIds = Array.isArray(
                                        (u as any).cityIds
                                      )
                                        ? (u as any).cityIds
                                        : (u as any).cityId
                                          ? [(u as any).cityId]
                                          : [];
                                      return (
                                        editUserForm.cityId !== 'none' &&
                                        pmCityIds.includes(
                                          editUserForm.cityId as string
                                        )
                                      );
                                    })
                                    .sort((a: any, b: any) =>
                                      `${a.firstName || ''} ${a.lastName || ''}`
                                        .trim()
                                        .localeCompare(
                                          `${b.firstName || ''} ${b.lastName || ''}`.trim()
                                        )
                                    )
                                    .map((pm: any) => (
                                      <SelectItem key={pm.id} value={pm.id}>
                                        {pm.firstName} {pm.lastName} (PM)
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-normal text-gray-600">
                                HOD
                              </Label>
                              <Select
                                value={editUserForm.assignedByHodId}
                                onValueChange={v =>
                                  setEditUserForm({
                                    ...editUserForm,
                                    assignedByHodId: v as any,
                                    assignedByPmId:
                                      v !== 'none'
                                        ? 'none'
                                        : editUserForm.assignedByPmId,
                                  })
                                }
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select HOD" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No HOD</SelectItem>
                                  {users
                                    .filter((u: any) => {
                                      if (u.role !== 'hod' && u.role !== 'dpm')
                                        return false;
                                      const hodCityIds = Array.isArray(
                                        (u as any).cityIds
                                      )
                                        ? (u as any).cityIds
                                        : (u as any).cityId
                                          ? [(u as any).cityId]
                                          : [];
                                      return (
                                        editUserForm.cityId !== 'none' &&
                                        hodCityIds.includes(
                                          editUserForm.cityId as string
                                        )
                                      );
                                    })
                                    .sort((a: any, b: any) =>
                                      `${a.firstName || ''} ${a.lastName || ''}`
                                        .trim()
                                        .localeCompare(
                                          `${b.firstName || ''} ${b.lastName || ''}`.trim()
                                        )
                                    )
                                    .map((hod: any) => (
                                      <SelectItem key={hod.id} value={hod.id}>
                                        {hod.firstName} {hod.lastName} (
                                        {hod.role === 'dpm' ? 'DPM' : 'HOD'})
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Staff is assigned under the selected PM or HOD
                            (leader).
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditUserModalOpen(false);
                    setSelectedUser(null);
                    setEditUserForm({
                      firstName: '',
                      lastName: '',
                      email: '',
                      username: '',
                      password: '',
                      confirmPassword: '',
                      teamId: 'none',
                      cityId: 'none',
                      cityIds: [],
                      role: 'hod',
                      allowTeamView: false,
                      assignedByPmId: 'none',
                      assignedByHodId: 'none',
                      isActive: true,
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!selectedUser) return;

                    // Validate username if provided
                    if (
                      editUserForm.username &&
                      editUserForm.username.trim().length > 0
                    ) {
                      const uname = editUserForm.username.trim().toLowerCase();
                      if (!/^[a-z0-9._-]{3,32}$/.test(uname)) {
                        toast({
                          title: 'Error',
                          description:
                            'Invalid username format. Allowed: letters, numbers, dot, underscore, hyphen (3-32 characters)',
                          variant: 'destructive',
                        });
                        return;
                      }
                    }

                    // Validate password if provided
                    if (
                      editUserForm.password &&
                      editUserForm.password.length > 0
                    ) {
                      if (editUserForm.password.length < 6) {
                        toast({
                          title: 'Error',
                          description: 'Password must be at least 6 characters',
                          variant: 'destructive',
                        });
                        return;
                      }
                      if (
                        editUserForm.password !== editUserForm.confirmPassword
                      ) {
                        toast({
                          title: 'Error',
                          description: 'Passwords do not match',
                          variant: 'destructive',
                        });
                        return;
                      }
                    }

                    // Validate city assignments
                    if (
                      editUserForm.role === 'gm' &&
                      editUserForm.cityId === 'none'
                    ) {
                      toast({
                        title: 'Error',
                        description: 'City is required for GM role',
                        variant: 'destructive',
                      });
                      return;
                    } else if (
                      (editUserForm.role === 'pm' ||
                        editUserForm.role === 'hod' ||
                        editUserForm.role === 'dpm') &&
                      editUserForm.cityIds.length === 0
                    ) {
                      toast({
                        title: 'Error',
                        description: `At least one city is required for ${editUserForm.role.toUpperCase()} role`,
                        variant: 'destructive',
                      });
                      return;
                    }

                    if (
                      isAdmin &&
                      editUserForm.isActive === false &&
                      (selectedUser as any).isActive !== false
                    ) {
                      try {
                        const res = await apiRequest(
                          'GET',
                          `/api/users/${selectedUser.id}/deactivation-impact`
                        );
                        const impact = await res.json();
                        if (impact.pendingCount > 0) {
                          setDeactivationImpact(impact);
                          setDeactivateConfirmOpen(true);
                          return;
                        }
                      } catch (e: any) {
                        toast({
                          title: 'Error',
                          description:
                            e?.message || 'Could not check pending tasks',
                          variant: 'destructive',
                        });
                        return;
                      }
                    }

                    updateUserMutation.mutate({
                      userId: selectedUser.id,
                      data: editUserForm,
                    });
                  }}
                  disabled={updateUserMutation.isPending}
                >
                  {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={deactivateConfirmOpen}
            onOpenChange={setDeactivateConfirmOpen}
          >
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Confirm deactivation</DialogTitle>
                <DialogDescription>
                  This user has {deactivationImpact?.pendingCount ?? 0} open
                  task(s) as assignee (not completed, not dropped). Those tasks are
                  hidden from non-admins when every assignee on the task is
                  inactive; shared tasks stay visible if any assignee is active.
                  Continue?
                </DialogDescription>
              </DialogHeader>
              {deactivationImpact &&
                deactivationImpact.pendingTasks.length > 0 && (
                  <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-muted-foreground">
                    {deactivationImpact.pendingTasks.map(t => (
                      <li key={t.id}>{t.title}</li>
                    ))}
                  </ul>
                )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDeactivateConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!selectedUser) return;
                    setDeactivateConfirmOpen(false);
                    updateUserMutation.mutate({
                      userId: selectedUser.id,
                      data: editUserForm,
                    });
                  }}
                  disabled={updateUserMutation.isPending}
                >
                  Deactivate
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Manage Cities/Teams Modal */}
          <Dialog
            open={isTeamsModalOpen}
            onOpenChange={open => {
              setIsTeamsModalOpen(open);
              if (!open) {
                // Reset state when modal closes
                setPendingCities([]);
                setPendingPrimaryTeam('none');
                setPendingExtraTeams([]);
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selectedUser?.role === 'gm'
                    ? 'Manage City Assignment'
                    : selectedUser?.role === 'pm' ||
                        selectedUser?.role === 'hod' ||
                        selectedUser?.role === 'dpm'
                      ? 'Manage Cities Assignment'
                      : 'Manage Team Assignment'}
                </DialogTitle>
                <DialogDescription>
                  {selectedUser?.role === 'gm'
                    ? `Assign city for ${selectedUser?.firstName} ${selectedUser?.lastName}`
                    : selectedUser?.role === 'pm' ||
                        selectedUser?.role === 'hod' ||
                        selectedUser?.role === 'dpm'
                      ? `Assign cities for ${selectedUser?.firstName} ${selectedUser?.lastName}`
                      : `Assign primary and additional teams for ${selectedUser?.firstName} ${selectedUser?.lastName}`}
                </DialogDescription>
              </DialogHeader>

              {selectedUser && (
                <div className="space-y-4">
                  {/* For GM: Show Single City Selector, For PM: Show Multiple Cities */}
                  {selectedUser.role === 'gm' ? (
                    <div>
                      <Label>
                        City <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={pendingPrimaryTeam}
                        onValueChange={setPendingPrimaryTeam}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                        <SelectContent>
                          {cities.map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {cities.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          No cities available. Create a city first.
                        </p>
                      )}
                    </div>
                  ) : selectedUser.role === 'pm' ||
                    selectedUser.role === 'hod' ||
                    selectedUser.role === 'dpm' ? (
                    <div>
                      <Label>
                        Cities (Select Multiple){' '}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="border rounded-md p-3 max-h-48 overflow-auto">
                        {cities.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No cities available. Create a city first.
                          </p>
                        ) : (
                          cities
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(city => (
                              <div
                                key={city.id}
                                className="flex items-center space-x-2 py-1"
                              >
                                <input
                                  type="checkbox"
                                  id={`manage-city-${city.id}`}
                                  checked={pendingCities.includes(city.id)}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      setPendingCities([
                                        ...pendingCities,
                                        city.id,
                                      ]);
                                    } else {
                                      setPendingCities(
                                        pendingCities.filter(
                                          id => id !== city.id
                                        )
                                      );
                                    }
                                  }}
                                  className="h-4 w-4"
                                />
                                <label
                                  htmlFor={`manage-city-${city.id}`}
                                  className="text-sm cursor-pointer flex-1"
                                >
                                  {city.name}
                                </label>
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  ) : (
                    /* For Team Members: Show Team Selector */
                    <>
                      <div>
                        <Label>Primary Team</Label>
                        <Select
                          value={pendingPrimaryTeam}
                          onValueChange={setPendingPrimaryTeam}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select team" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              No primary team
                            </SelectItem>
                            {teams.map(t => {
                              const allowed =
                                isAdmin || (isManager && myTeamId === t.id);
                              return (
                                <SelectItem
                                  key={t.id}
                                  value={t.id}
                                  disabled={!allowed}
                                >
                                  {t.name}
                                  {!allowed ? ' (not allowed)' : ''}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Additional Teams</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-auto border p-2 rounded">
                          {teams.map(t => {
                            const allowed =
                              isAdmin || (isManager && myTeamId === t.id);
                            const active = pendingExtraTeams.includes(t.id);
                            return (
                              <Button
                                key={t.id}
                                variant={active ? 'default' : 'outline'}
                                size="sm"
                                disabled={!allowed}
                                onClick={() => {
                                  if (!allowed) return;
                                  setPendingExtraTeams(prev =>
                                    prev.includes(t.id)
                                      ? prev.filter(id => id !== t.id)
                                      : [...prev, t.id]
                                  );
                                }}
                              >
                                {t.name}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsTeamsModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!selectedUser) return;
                    try {
                      // For GM: Update single city, For PM and HOD: Update multiple cities
                      if (selectedUser.role === 'gm') {
                        if (
                          !pendingPrimaryTeam ||
                          pendingPrimaryTeam === 'none'
                        ) {
                          toast({
                            title: 'Error',
                            description: 'City is required for GM role',
                            variant: 'destructive',
                          });
                          return;
                        }
                        await fetch(`/api/users/${selectedUser.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({
                            cityId: pendingPrimaryTeam,
                          }),
                        });
                        queryClient.invalidateQueries({
                          queryKey: ['/api/users'],
                        });
                        queryClient.invalidateQueries({
                          queryKey: ['/api/cities'],
                        });
                        toast({
                          title: 'Success',
                          description: 'City updated',
                        });
                        setIsTeamsModalOpen(false);
                      } else if (
                        selectedUser.role === 'pm' ||
                        selectedUser.role === 'hod' ||
                        selectedUser.role === 'dpm'
                      ) {
                        if (pendingCities.length === 0) {
                          toast({
                            title: 'Error',
                            description: `At least one city is required for ${selectedUser.role.toUpperCase()} role`,
                            variant: 'destructive',
                          });
                          return;
                        }
                        await fetch(`/api/users/${selectedUser.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({
                            cityIds: pendingCities,
                            cityId: pendingCities[0], // Keep first for backward compatibility
                          }),
                        });
                        // Invalidate and refetch users to get updated cityIds
                        await queryClient.invalidateQueries({
                          queryKey: ['/api/users'],
                        });
                        queryClient.invalidateQueries({
                          queryKey: ['/api/cities'],
                        });
                        toast({
                          title: 'Success',
                          description: 'Cities updated',
                        });
                        setIsTeamsModalOpen(false);
                        setPendingCities([]); // Reset after save
                        setSelectedUser(null); // Clear selected user
                      } else {
                        // For Team Members: Update teams
                        const allowedPrimary =
                          isAdmin ||
                          (isManager &&
                            (pendingPrimaryTeam === 'none' ||
                              pendingPrimaryTeam === myTeamId));
                        if (!allowedPrimary) {
                          toast({
                            title: 'Not allowed',
                            description:
                              'Managers can only set primary team to their own team or none.',
                            variant: 'destructive',
                          });
                          return;
                        }
                        // Update primary team
                        await fetch(
                          `/api/users/${selectedUser.id}/primary-team`,
                          {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                              teamId:
                                pendingPrimaryTeam === 'none'
                                  ? null
                                  : pendingPrimaryTeam,
                            }),
                          }
                        );
                        // Fetch current memberships
                        const current = await (
                          await fetch(`/api/users/${selectedUser.id}/teams`, {
                            credentials: 'include',
                          })
                        ).json();
                        const curExtra: string[] = current.extraTeamIds || [];
                        // Filter extras per permissions
                        const allowedAddIds = pendingExtraTeams.filter(
                          id => isAdmin || (isManager && id === myTeamId)
                        );
                        const allowedRemoveIds = curExtra.filter(
                          (id: string) =>
                            isAdmin || (isManager && id === myTeamId)
                        );
                        // Add new ones
                        for (const id of allowedAddIds.filter(
                          id => !curExtra.includes(id)
                        )) {
                          await fetch(`/api/users/${selectedUser.id}/teams`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ teamId: id }),
                          });
                        }
                        // Remove unchecked (only within allowed scope)
                        for (const id of allowedRemoveIds.filter(
                          (id: string) => !pendingExtraTeams.includes(id)
                        )) {
                          await fetch(
                            `/api/users/${selectedUser.id}/teams/${id}`,
                            { method: 'DELETE', credentials: 'include' }
                          );
                        }
                        queryClient.invalidateQueries({
                          queryKey: ['/api/users'],
                        });
                        toast({
                          title: 'Success',
                          description: 'Teams updated',
                        });
                        setIsTeamsModalOpen(false);
                      }
                    } catch (e) {
                      toast({
                        title: 'Error',
                        description: 'Failed to update assignment',
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Manage Projects Modal */}
          <Dialog
            open={isProjectsModalOpen}
            onOpenChange={open => {
              setIsProjectsModalOpen(open);
              if (!open) {
                setPendingProjects([]);
                setSelectedUser(null);
              }
            }}
          >
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  Manage Projects for {selectedUser?.firstName}{' '}
                  {selectedUser?.lastName}
                </DialogTitle>
                <DialogDescription>
                  {selectedUser?.role === 'gm'
                    ? "Assign projects to this GM. Projects are filtered by the GM's assigned city."
                    : selectedUser?.role === 'pm'
                      ? "Assign projects to this PM. Projects are filtered by the PM's assigned cities."
                      : 'Assign projects to this HOD/DPM. Projects are filtered by their assigned cities.'}
                </DialogDescription>
              </DialogHeader>

              {selectedUser &&
                (() => {
                  // Calculate selection state for indeterminate checkbox
                  // Get user's assigned cities
                  let userCityIds: string[] = [];
                  if (selectedUser.role === 'gm') {
                    if ((selectedUser as any).cityId) {
                      userCityIds = [(selectedUser as any).cityId];
                    }
                  } else if (
                    selectedUser.role === 'pm' ||
                    selectedUser.role === 'hod' ||
                    selectedUser.role === 'dpm'
                  ) {
                    if ((selectedUser as any).cityIds) {
                      if (typeof (selectedUser as any).cityIds === 'string') {
                        try {
                          userCityIds = JSON.parse(
                            (selectedUser as any).cityIds
                          );
                        } catch (e) {
                          userCityIds = [];
                        }
                      } else if (Array.isArray((selectedUser as any).cityIds)) {
                        userCityIds = (selectedUser as any).cityIds;
                      }
                    }
                    if (
                      userCityIds.length === 0 &&
                      (selectedUser as any).cityId
                    ) {
                      userCityIds = [(selectedUser as any).cityId];
                    }
                  }

                  // Filter projects by user's cities
                  const availableProjects = teams.filter((team: any) => {
                    if (!team.cityId) return false;
                    return userCityIds.includes(team.cityId);
                  });

                  if (userCityIds.length === 0) {
                    return (
                      <div className="py-4">
                        <p className="text-sm text-amber-600">
                          This user has no cities assigned. Please assign cities
                          first before managing projects.
                        </p>
                      </div>
                    );
                  }

                  if (availableProjects.length === 0) {
                    return (
                      <div className="py-4">
                        <p className="text-sm text-gray-500">
                          No projects found in the assigned cities. Create
                          projects in these cities first.
                        </p>
                      </div>
                    );
                  }

                  const allSelected =
                    availableProjects.length > 0 &&
                    availableProjects.every((p: any) =>
                      pendingProjects.includes(p.id)
                    );

                  return (
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label>Projects in assigned cities</Label>
                          <div className="flex items-center space-x-2">
                            <input
                              ref={selectAllProjectsRef}
                              type="checkbox"
                              id="select-all-projects"
                              checked={allSelected}
                              onChange={e => {
                                if (e.target.checked) {
                                  // Select all projects
                                  const allProjectIds = availableProjects.map(
                                    (p: any) => p.id
                                  );
                                  const uniqueIds = Array.from(
                                    new Set([
                                      ...pendingProjects,
                                      ...allProjectIds,
                                    ])
                                  );
                                  setPendingProjects(uniqueIds);
                                } else {
                                  // Deselect all projects
                                  const allProjectIds = availableProjects.map(
                                    (p: any) => p.id
                                  );
                                  setPendingProjects(
                                    pendingProjects.filter(
                                      id => !allProjectIds.includes(id)
                                    )
                                  );
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <label
                              htmlFor="select-all-projects"
                              className="text-sm font-medium cursor-pointer"
                            >
                              Select All
                            </label>
                          </div>
                        </div>
                        <div className="border rounded-md p-3 max-h-96 overflow-auto mt-2">
                          {availableProjects
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((project: any) => {
                              const isAssigned = pendingProjects.includes(
                                project.id
                              );
                              const cityName =
                                cities.find(c => c.id === project.cityId)
                                  ?.name || 'Unknown City';

                              return (
                                <div
                                  key={project.id}
                                  className="flex items-start space-x-3 py-2 border-b last:border-b-0"
                                >
                                  <input
                                    type="checkbox"
                                    id={`manage-project-${project.id}`}
                                    checked={isAssigned}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setPendingProjects([
                                          ...pendingProjects,
                                          project.id,
                                        ]);
                                      } else {
                                        setPendingProjects(
                                          pendingProjects.filter(
                                            id => id !== project.id
                                          )
                                        );
                                      }
                                    }}
                                    className="h-4 w-4 mt-1"
                                  />
                                  <label
                                    htmlFor={`manage-project-${project.id}`}
                                    className="text-sm cursor-pointer flex-1"
                                  >
                                    <div className="font-medium">
                                      {project.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      City: {cityName}
                                      {project.description &&
                                        ` • ${project.description}`}
                                    </div>
                                  </label>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsProjectsModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!selectedUser) return;
                    try {
                      // Get all projects that need to be updated
                      const allProjects = teams.filter((team: any) => {
                        if (!team.cityId) return false;
                        let userCityIds: string[] = [];
                        if (selectedUser.role === 'gm') {
                          if ((selectedUser as any).cityId) {
                            userCityIds = [(selectedUser as any).cityId];
                          }
                        } else {
                          if ((selectedUser as any).cityIds) {
                            if (
                              typeof (selectedUser as any).cityIds === 'string'
                            ) {
                              try {
                                userCityIds = JSON.parse(
                                  (selectedUser as any).cityIds
                                );
                              } catch (e) {
                                userCityIds = [];
                              }
                            } else if (
                              Array.isArray((selectedUser as any).cityIds)
                            ) {
                              userCityIds = (selectedUser as any).cityIds;
                            }
                          }
                        }
                        return userCityIds.includes(team.cityId);
                      });

                      // Update each project
                      for (const project of allProjects) {
                        const shouldBeAssigned = pendingProjects.includes(
                          project.id
                        );
                        const isCurrentlyAssigned = (() => {
                          if (selectedUser.role === 'gm') {
                            return project.gmId === selectedUser.id;
                          } else if (selectedUser.role === 'pm') {
                            return (
                              project.pmId === selectedUser.id ||
                              (Array.isArray(project.pmIds) &&
                                project.pmIds.includes(selectedUser.id))
                            );
                          } else if (selectedUser.role === 'hod') {
                            return (
                              project.hodId === selectedUser.id ||
                              (Array.isArray(project.hodIds) &&
                                project.hodIds.includes(selectedUser.id))
                            );
                          } else if (selectedUser.role === 'dpm') {
                            const p = project as any;
                            return (
                              p.dpmId === selectedUser.id ||
                              (Array.isArray(p.dpmIds) &&
                                p.dpmIds.includes(selectedUser.id)) ||
                              project.hodId === selectedUser.id ||
                              (Array.isArray(project.hodIds) &&
                                project.hodIds.includes(selectedUser.id))
                            );
                          }
                          return false;
                        })();

                        if (shouldBeAssigned && !isCurrentlyAssigned) {
                          // Add user to project
                          const updates: any = {};
                          if (selectedUser.role === 'gm') {
                            updates.gmId = selectedUser.id;
                          } else if (selectedUser.role === 'pm') {
                            const currentPmIds = Array.isArray(project.pmIds)
                              ? project.pmIds
                              : project.pmId
                                ? [project.pmId]
                                : [];
                            if (!currentPmIds.includes(selectedUser.id)) {
                              updates.pmIds = [
                                ...currentPmIds,
                                selectedUser.id,
                              ];
                              // Keep pmId for backward compatibility (use first PM)
                              if (currentPmIds.length === 0) {
                                updates.pmId = selectedUser.id;
                              }
                            }
                          } else if (selectedUser.role === 'hod') {
                            const currentHodIds = Array.isArray(project.hodIds)
                              ? project.hodIds
                              : project.hodId
                                ? [project.hodId]
                                : [];
                            if (!currentHodIds.includes(selectedUser.id)) {
                              updates.hodIds = [
                                ...currentHodIds,
                                selectedUser.id,
                              ];
                              // Keep hodId for backward compatibility (use first HOD)
                              if (currentHodIds.length === 0) {
                                updates.hodId = selectedUser.id;
                              }
                            }
                          } else if (selectedUser.role === 'dpm') {
                            const p = project as any;
                            const currentDpmIds = Array.isArray(p.dpmIds)
                              ? p.dpmIds
                              : p.dpmId
                                ? [p.dpmId]
                                : [];
                            if (!currentDpmIds.includes(selectedUser.id)) {
                              updates.dpmIds = [
                                ...currentDpmIds,
                                selectedUser.id,
                              ];
                              if (currentDpmIds.length === 0) {
                                updates.dpmId = selectedUser.id;
                              }
                            }
                          }

                          if (Object.keys(updates).length > 0) {
                            // Update teams table (projects in UI are stored as teams in DB)
                            const response = await fetch(
                              `/api/teams/${project.id}`,
                              {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify(updates),
                              }
                            );

                            if (!response.ok) {
                              let errorText = await response.text();
                              try {
                                const errorData = JSON.parse(errorText);
                                if (
                                  errorData.errors &&
                                  Array.isArray(errorData.errors)
                                ) {
                                  errorText = errorData.errors.join(' ');
                                } else if (errorData.message) {
                                  errorText = errorData.message;
                                }
                              } catch {
                                // If parsing fails, use the original text
                              }
                              throw new Error(
                                `Failed to update project ${project.name}: ${errorText}`
                              );
                            }
                          }
                        } else if (!shouldBeAssigned && isCurrentlyAssigned) {
                          // Remove user from project
                          const updates: any = {};
                          if (selectedUser.role === 'gm') {
                            updates.gmId = null;
                          } else if (selectedUser.role === 'pm') {
                            const currentPmIds = Array.isArray(project.pmIds)
                              ? project.pmIds
                              : project.pmId
                                ? [project.pmId]
                                : [];
                            const newPmIds = currentPmIds.filter(
                              (id: string) => id !== selectedUser.id
                            );
                            updates.pmIds = newPmIds;
                            // Update pmId for backward compatibility
                            updates.pmId =
                              newPmIds.length > 0 ? newPmIds[0] : null;
                          } else if (selectedUser.role === 'hod') {
                            const currentHodIds = Array.isArray(project.hodIds)
                              ? project.hodIds
                              : project.hodId
                                ? [project.hodId]
                                : [];
                            const newHodIds = currentHodIds.filter(
                              (id: string) => id !== selectedUser.id
                            );
                            updates.hodIds = newHodIds;
                            // Update hodId for backward compatibility
                            updates.hodId =
                              newHodIds.length > 0 ? newHodIds[0] : null;
                          } else if (selectedUser.role === 'dpm') {
                            const p = project as any;
                            const currentDpmIds = Array.isArray(p.dpmIds)
                              ? p.dpmIds
                              : p.dpmId
                                ? [p.dpmId]
                                : [];
                            if (currentDpmIds.includes(selectedUser.id)) {
                              const newDpmIds = currentDpmIds.filter(
                                (id: string) => id !== selectedUser.id
                              );
                              updates.dpmIds = newDpmIds;
                              updates.dpmId =
                                newDpmIds.length > 0 ? newDpmIds[0] : null;
                            } else {
                              const currentHodIds = Array.isArray(
                                project.hodIds
                              )
                                ? project.hodIds
                                : project.hodId
                                  ? [project.hodId]
                                  : [];
                              const newHodIds = currentHodIds.filter(
                                (id: string) => id !== selectedUser.id
                              );
                              updates.hodIds = newHodIds;
                              updates.hodId =
                                newHodIds.length > 0 ? newHodIds[0] : null;
                            }
                          }

                          // Update teams table (projects in UI are stored as teams in DB)
                          const response = await fetch(
                            `/api/teams/${project.id}`,
                            {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify(updates),
                            }
                          );

                          if (!response.ok) {
                            let errorText = await response.text();
                            try {
                              const errorData = JSON.parse(errorText);
                              if (
                                errorData.errors &&
                                Array.isArray(errorData.errors)
                              ) {
                                errorText = errorData.errors.join(' ');
                              } else if (errorData.message) {
                                errorText = errorData.message;
                              }
                            } catch {
                              // If parsing fails, use the original text
                            }
                            throw new Error(
                              `Failed to update project ${project.name}: ${errorText}`
                            );
                          }
                        }
                      }

                      // Invalidate and refetch queries to refresh data
                      await queryClient.invalidateQueries({
                        queryKey: ['/api/projects'],
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ['/api/teams'],
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ['/api/users'],
                      });

                      // Refetch teams to get updated project data
                      await queryClient.refetchQueries({
                        queryKey: ['/api/teams'],
                      });

                      toast({
                        title: 'Success',
                        description: 'Projects updated successfully',
                      });
                      setIsProjectsModalOpen(false);
                    } catch (error: any) {
                      // Handle specific error about active tasks
                      let errorMessage =
                        error.message || 'Failed to update projects';
                      try {
                        const errorData = JSON.parse(
                          error.message.split(': ')[1] || '{}'
                        );
                        if (
                          errorData.errors &&
                          Array.isArray(errorData.errors)
                        ) {
                          errorMessage = errorData.errors.join('\n');
                        } else if (errorData.message) {
                          errorMessage = errorData.message;
                        }
                      } catch {
                        // If parsing fails, use the original message
                      }

                      toast({
                        title: 'Update Failed',
                        description: errorMessage,
                        variant: 'destructive',
                        duration: 6000, // Show longer for important errors
                      });
                    }
                  }}
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Credentials Modal (Admin/Manager) */}
          <Dialog open={isCredsModalOpen} onOpenChange={setIsCredsModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>User Credentials</DialogTitle>
                <DialogDescription>
                  View username and reset password for {selectedUser?.firstName}{' '}
                  {selectedUser?.lastName}
                </DialogDescription>
              </DialogHeader>

              {selectedUser && (
                <div className="space-y-4">
                  <div>
                    <Label>Username</Label>
                    <Input
                      value={newUsername}
                      onChange={e => setNewUsername(e.target.value)}
                      placeholder="Enter new username"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Allowed: letters, numbers, dot, underscore, hyphen
                    </p>
                  </div>
                  <div>
                    <Label>New Password</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input
                      type="password"
                      value={confirmNewPassword}
                      onChange={e => setConfirmNewPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCredsModalOpen(false);
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setNewUsername('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!selectedUser) return;
                    const uname = newUsername.trim().toLowerCase();
                    if (!/^[a-z0-9._-]{3,32}$/.test(uname)) {
                      toast({
                        title: 'Error',
                        description: 'Invalid username format',
                        variant: 'destructive',
                      });
                      return;
                    }
                    updateUsernameMutation.mutate({
                      userId: selectedUser.id,
                      username: uname,
                    });
                  }}
                  disabled={updateUsernameMutation.isPending}
                >
                  {updateUsernameMutation.isPending
                    ? 'Saving...'
                    : 'Update Username'}
                </Button>
                <Button
                  onClick={() => {
                    if (!selectedUser) return;
                    if (newPassword.length < 6) {
                      toast({
                        title: 'Error',
                        description: 'Password must be at least 6 characters',
                        variant: 'destructive',
                      });
                      return;
                    }
                    if (newPassword !== confirmNewPassword) {
                      toast({
                        title: 'Error',
                        description: "Passwords don't match",
                        variant: 'destructive',
                      });
                      return;
                    }
                    resetPasswordMutation.mutate({
                      userId: selectedUser.id,
                      newPassword,
                    });
                  }}
                  disabled={resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending
                    ? 'Updating...'
                    : 'Update Password'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Bulk Add CSV Modal */}
          <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Add Staff (CSV)</DialogTitle>
                <DialogDescription>
                  Paste CSV with headers:
                  firstName,lastName,email,password,role,teamId,allowTeamView.
                  Managers are limited to their own team and team_member role.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const csv =
                        'firstName,lastName,email,password,role,teamId,allowTeamView\nJane,Doe,jane@example.com,Passw0rd,team_member,none,true';
                      const blob = new Blob([csv], {
                        type: 'text/csv;charset=utf-8;',
                      });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'staff_template.csv';
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Download CSV Template
                  </Button>
                  <div className="text-xs text-gray-500">
                    Use the template to prepare your upload.
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Upload CSV file</Label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setBulkFileName(f.name);
                      const reader = new FileReader();
                      reader.onload = () =>
                        setBulkText(String(reader.result || ''));
                      reader.readAsText(f);
                    }}
                  />
                  {bulkFileName && (
                    <div className="text-xs text-gray-600">
                      Selected: {bulkFileName}
                    </div>
                  )}
                </div>
                <Label>Or paste CSV</Label>
                <textarea
                  className="w-full h-48 border rounded p-2 text-sm"
                  value={bulkText}
                  onChange={e => setBulkText(e.target.value)}
                />
                {bulkSubmitting && (
                  <div className="mt-2">
                    <div className="h-2 bg-gray-200 rounded">
                      <div
                        className="h-2 bg-blue-600 rounded"
                        style={{
                          width: `${bulkTotal ? Math.min(100, Math.round((bulkDone / Math.max(1, bulkTotal)) * 100)) : 0}%`,
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {bulkDone}/{bulkTotal} processed
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsBulkOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={bulkCreateFromCSV} disabled={bulkSubmitting}>
                  {bulkSubmitting ? 'Importing...' : 'Import'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Create Member Modal */}
          <Dialog
            open={isCreateMemberOpen}
            onOpenChange={setIsCreateMemberOpen}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
                <DialogDescription>
                  Create a new staff member. Managers can only add to their own
                  team.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={memberForm.firstName}
                      onChange={e => {
                        const firstName = e.target.value;
                        const next = { ...memberForm, firstName };
                        const suggested = `${firstName} ${memberForm.lastName}`
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '')
                          .replace(/[^a-z0-9._-]/g, '');
                        if (suggested) next.username = suggested;
                        setMemberForm(next);
                      }}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={memberForm.lastName}
                      onChange={e => {
                        const lastName = e.target.value;
                        const next = { ...memberForm, lastName };
                        const suggested = `${memberForm.firstName} ${lastName}`
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, '')
                          .replace(/[^a-z0-9._-]/g, '');
                        if (suggested) next.username = suggested;
                        setMemberForm(next);
                      }}
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={memberForm.email}
                    onChange={e =>
                      setMemberForm({ ...memberForm, email: e.target.value })
                    }
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={memberForm.username ?? ''}
                    onChange={e =>
                      setMemberForm({ ...memberForm, username: e.target.value })
                    }
                    placeholder="Login username (stored in database)"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This username will be stored in the database and used for
                    login. Edit if needed. Leave empty to auto-generate from
                    first and last name.
                  </p>
                  {debouncedUsernameCheck.length >= 3 && (
                    <p className="text-xs mt-1">
                      {usernameCheckLoading ? (
                        <span className="text-muted-foreground">
                          Checking availability...
                        </span>
                      ) : usernameAvailability ? (
                        usernameAvailability.available ? (
                          <span className="text-green-600 font-medium">
                            Username available
                          </span>
                        ) : (
                          <span className="text-red-600 font-medium">
                            Username already registered. Please change the
                            username.
                          </span>
                        )
                      ) : null}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={memberForm.password}
                      onChange={e =>
                        setMemberForm({
                          ...memberForm,
                          password: e.target.value,
                        })
                      }
                      placeholder="Min 6 characters"
                    />
                  </div>
                  <div>
                    <Label>Confirm Password</Label>
                    <Input
                      type="password"
                      value={memberForm.confirmPassword}
                      onChange={e =>
                        setMemberForm({
                          ...memberForm,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder="Re-enter password"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role</Label>
                    <Select
                      value={memberForm.role}
                      onValueChange={v => {
                        const newRole = v as any;
                        // Reset assignment fields when role changes
                        if (newRole !== 'staff') {
                          setMemberForm({
                            ...memberForm,
                            role: newRole,
                            assignedByPmId: 'none',
                            assignedByHodId: 'none',
                          });
                        } else {
                          // If switching to staff and current user is PM/HOD, auto-select themselves
                          const newForm: any = { ...memberForm, role: newRole };
                          if (user?.role === 'pm' && user?.id) {
                            newForm.assignedByPmId = user.id;
                            newForm.assignedByHodId = 'none';
                          } else if (
                            (user?.role === 'hod' || user?.role === 'dpm') &&
                            user?.id
                          ) {
                            newForm.assignedByHodId = user.id;
                            newForm.assignedByPmId = 'none';
                          }
                          setMemberForm(newForm);
                        }
                      }}
                      disabled={isManager}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* <SelectItem value="team_member">Team Member</SelectItem> */}
                        <SelectItem value="admin" disabled={!isAdmin}>
                          Admin
                        </SelectItem>
                        <SelectItem value="hod" disabled={!isAdmin}>
                          HOD (Head of Department)
                        </SelectItem>
                        <SelectItem value="dpm" disabled={!isAdmin}>
                          DPM
                        </SelectItem>
                        <SelectItem value="pm" disabled={!isAdmin}>
                          PM (Project Manager)
                        </SelectItem>
                        <SelectItem value="gm" disabled={!isAdmin}>
                          GM (General Manager)
                        </SelectItem>
                        <SelectItem
                          value="staff"
                          disabled={
                            !isAdmin &&
                            user?.role !== 'pm' &&
                            user?.role !== 'hod' &&
                            user?.role !== 'dpm'
                          }
                        >
                          Staff
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Team Member role is disabled - Allow Team View is no longer needed
                    {memberForm.role === 'team_member' && (
                      <div className="mt-3 flex items-center gap-3">
                        <Label className="text-sm">Allow Team View</Label>
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={memberForm.allowTeamView}
                          onChange={e =>
                            setMemberForm({
                              ...memberForm,
                              allowTeamView: e.target.checked,
                            })
                          }
                        />
                      </div>
                    )}
                    */}
                  </div>
                  <div>
                    {memberForm.role === 'admin' ? (
                      <div className="text-sm text-gray-500 py-2">
                        Admin users have full system access and don't require
                        city or team assignments.
                      </div>
                    ) : memberForm.role === 'gm' ? (
                      <>
                        <Label>
                          City <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={memberForm.cityId}
                          onValueChange={v =>
                            setMemberForm({ ...memberForm, cityId: v as any })
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent>
                            {cities
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {cities.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            No cities available. Create a city first.
                          </p>
                        )}
                      </>
                    ) : memberForm.role === 'staff' ? (
                      <>
                        {/* Staff: first select city, then PM/HOD list will be filtered by that city */}
                        <Label>
                          City <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={memberForm.cityId}
                          onValueChange={v =>
                            setMemberForm({
                              ...memberForm,
                              cityId: v as any,
                              // Reset PM/HOD selection when city changes
                              assignedByPmId: 'none',
                              assignedByHodId: 'none',
                            })
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select city</SelectItem>
                            {cities
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {cities.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            No cities available. Create a city first.
                          </p>
                        )}
                        {memberForm.cityId === 'none' ? (
                          <p className="text-xs text-amber-600 mt-2">
                            Please select a city to see available PM and HOD
                            options.
                          </p>
                        ) : (
                          <>
                            <Label className="mt-4 block">
                              Assign Under{' '}
                              <span className="text-red-500">*</span>
                            </Label>
                            <div className="space-y-3 mt-1">
                              <div>
                                <Label className="text-sm font-normal mb-2 block">
                                  Project Manager (PM)
                                  {user?.role === 'pm' &&
                                    memberForm.assignedByPmId === user.id && (
                                      <span className="text-xs text-blue-600 ml-2">
                                        (You)
                                      </span>
                                    )}
                                </Label>
                                <Select
                                  value={memberForm.assignedByPmId}
                                  onValueChange={v =>
                                    setMemberForm({
                                      ...memberForm,
                                      assignedByPmId: v as any,
                                      // Clear HOD if PM is selected
                                      assignedByHodId:
                                        v !== 'none'
                                          ? 'none'
                                          : memberForm.assignedByHodId,
                                    })
                                  }
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select PM" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No PM</SelectItem>
                                    {users
                                      .filter((u: any) => {
                                        if (u.role !== 'pm') return false;
                                        // Filter PMs by selected staff city
                                        const pmCityIds = Array.isArray(
                                          (u as any).cityIds
                                        )
                                          ? (u as any).cityIds
                                          : (u as any).cityId
                                            ? [(u as any).cityId]
                                            : [];
                                        return pmCityIds.includes(
                                          memberForm.cityId as string
                                        );
                                      })
                                      .sort((a: any, b: any) => {
                                        const nameA = `${a.firstName || ''} ${
                                          a.lastName || ''
                                        }`.trim();
                                        const nameB = `${b.firstName || ''} ${
                                          b.lastName || ''
                                        }`.trim();
                                        return nameA.localeCompare(nameB);
                                      })
                                      .map((pm: any) => (
                                        <SelectItem key={pm.id} value={pm.id}>
                                          {pm.firstName} {pm.lastName} (PM)
                                          {pm.id === user?.id && ' - You'}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="text-xs text-gray-500 mb-2">
                                OR
                              </div>
                              <div>
                                <Label className="text-sm font-normal mb-2 block">
                                  Head of Department (HOD)
                                  {(user?.role === 'hod' ||
                                    user?.role === 'dpm') &&
                                    memberForm.assignedByHodId === user.id && (
                                      <span className="text-xs text-blue-600 ml-2">
                                        (You)
                                      </span>
                                    )}
                                </Label>
                                <Select
                                  value={memberForm.assignedByHodId}
                                  onValueChange={v =>
                                    setMemberForm({
                                      ...memberForm,
                                      assignedByHodId: v as any,
                                      // Clear PM if HOD is selected
                                      assignedByPmId:
                                        v !== 'none'
                                          ? 'none'
                                          : memberForm.assignedByPmId,
                                    })
                                  }
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select HOD" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No HOD</SelectItem>
                                    {users
                                      .filter((u: any) => {
                                        if (
                                          u.role !== 'hod' &&
                                          u.role !== 'dpm'
                                        ) {
                                          return false;
                                        }
                                        const hodCityIds = Array.isArray(
                                          (u as any).cityIds
                                        )
                                          ? (u as any).cityIds
                                          : (u as any).cityId
                                            ? [(u as any).cityId]
                                            : [];
                                        return hodCityIds.includes(
                                          memberForm.cityId as string
                                        );
                                      })
                                      .sort((a: any, b: any) => {
                                        const nameA = `${a.firstName || ''} ${
                                          a.lastName || ''
                                        }`.trim();
                                        const nameB = `${b.firstName || ''} ${
                                          b.lastName || ''
                                        }`.trim();
                                        return nameA.localeCompare(nameB);
                                      })
                                      .map((hod: any) => (
                                        <SelectItem key={hod.id} value={hod.id}>
                                          {hod.firstName} {hod.lastName} (
                                          {hod.role === 'dpm' ? 'DPM' : 'HOD'})
                                          {hod.id === user?.id && ' - You'}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              {memberForm.assignedByPmId === 'none' &&
                                memberForm.assignedByHodId === 'none' && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    Please select either a PM or HOD to assign
                                    this staff member under.
                                  </p>
                                )}
                              {(memberForm.assignedByPmId !== 'none' ||
                                memberForm.assignedByHodId !== 'none') && (
                                <p className="text-xs text-green-600 mt-1">
                                  Staff will be assigned under the selected{' '}
                                  {memberForm.assignedByPmId !== 'none'
                                    ? 'PM'
                                    : 'HOD'}
                                  .
                                </p>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    ) : memberForm.role === 'pm' ||
                      memberForm.role === 'hod' ||
                      memberForm.role === 'dpm' ? (
                      <>
                        <Label>
                          Cities (Select Multiple){' '}
                          <span className="text-red-500">*</span>
                        </Label>
                        <div className="border rounded-md p-3 max-h-48 overflow-auto">
                          {cities.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              No cities available. Create a city first.
                            </p>
                          ) : (
                            (() => {
                              const sortedCities = [...cities].sort((a, b) =>
                                a.name.localeCompare(b.name)
                              );
                              const allCityIds = sortedCities.map(c => c.id);
                              const allSelected =
                                allCityIds.length > 0 &&
                                allCityIds.every(id =>
                                  memberForm.cityIds.includes(id)
                                );

                              return (
                                <>
                                  {/* HOD-only: show Select All */}
                                  {(memberForm.role === 'hod' ||
                                    memberForm.role === 'dpm') && (
                                    <div className="flex items-center space-x-2 py-1 border-b mb-2 pb-2">
                                      <input
                                        type="checkbox"
                                        id="member-city-select-all"
                                        checked={allSelected}
                                        onChange={e => {
                                          const checked = e.target.checked;
                                          setMemberForm({
                                            ...memberForm,
                                            cityIds: checked ? allCityIds : [],
                                            cityId:
                                              checked && allCityIds.length > 0
                                                ? allCityIds[0]
                                                : 'none',
                                          });
                                        }}
                                        className="h-4 w-4"
                                      />
                                      <label
                                        htmlFor="member-city-select-all"
                                        className="text-sm cursor-pointer flex-1 font-medium"
                                      >
                                        Select all cities
                                      </label>
                                    </div>
                                  )}

                                  {sortedCities.map(city => (
                                    <div
                                      key={city.id}
                                      className="flex items-center space-x-2 py-1"
                                    >
                                      <input
                                        type="checkbox"
                                        id={`member-city-${city.id}`}
                                        checked={memberForm.cityIds.includes(
                                          city.id
                                        )}
                                        onChange={e => {
                                          if (e.target.checked) {
                                            setMemberForm({
                                              ...memberForm,
                                              cityIds: [
                                                ...memberForm.cityIds,
                                                city.id,
                                              ],
                                              cityId: city.id, // Keep first city for backward compatibility
                                            });
                                          } else {
                                            const nextCityIds =
                                              memberForm.cityIds.filter(
                                                id => id !== city.id
                                              );
                                            setMemberForm({
                                              ...memberForm,
                                              cityIds: nextCityIds,
                                              cityId:
                                                nextCityIds.length === 0
                                                  ? 'none'
                                                  : memberForm.cityId,
                                            });
                                          }
                                        }}
                                        className="h-4 w-4"
                                      />
                                      <label
                                        htmlFor={`member-city-${city.id}`}
                                        className="text-sm cursor-pointer flex-1"
                                      >
                                        {city.name}
                                      </label>
                                    </div>
                                  ))}
                                </>
                              );
                            })()
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <Label>Primary Team</Label>
                        <Select
                          value={memberForm.teamId}
                          onValueChange={v =>
                            setMemberForm({ ...memberForm, teamId: v as any })
                          }
                          disabled={isManager && !!myTeamId}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select team" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              No primary team
                            </SelectItem>
                            {teams
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(t => (
                                <SelectItem
                                  key={t.id}
                                  value={t.id}
                                  disabled={isManager && myTeamId !== t.id}
                                >
                                  {t.name}
                                  {isManager && myTeamId !== t.id
                                    ? ' (not allowed)'
                                    : ''}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {isManager && !managedTeam && (
                          <p className="text-xs text-amber-600 mt-1">
                            You don't have a managed team assigned. Contact an
                            admin.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  {/* Close grid (role + primary team) */}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsCreateMemberOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if ((memberForm.username ?? '').trim().length > 0) {
                      const uname = (memberForm.username ?? '')
                        .trim()
                        .toLowerCase();
                      if (!/^[a-z0-9._-]{3,32}$/.test(uname)) {
                        toast({
                          title: 'Error',
                          description:
                            'Invalid username. Use 3–32 characters: letters, numbers, dot, underscore, hyphen.',
                          variant: 'destructive',
                        });
                        return;
                      }
                    }
                    if (memberForm.password.length < 6) {
                      toast({
                        title: 'Error',
                        description: 'Password must be at least 6 characters',
                        variant: 'destructive',
                      });
                      return;
                    }
                    if (memberForm.password !== memberForm.confirmPassword) {
                      toast({
                        title: 'Error',
                        description: 'Passwords do not match',
                        variant: 'destructive',
                      });
                      return;
                    }
                    if (memberForm.role === 'admin') {
                      // Admin doesn't need city or team assignments
                    } else if (memberForm.role === 'staff') {
                      // Staff must have a city and be assigned to either PM or HOD
                      if (memberForm.cityId === 'none') {
                        toast({
                          title: 'Error',
                          description: 'City is required for Staff role',
                          variant: 'destructive',
                        });
                        return;
                      }
                      if (
                        memberForm.assignedByPmId === 'none' &&
                        memberForm.assignedByHodId === 'none'
                      ) {
                        toast({
                          title: 'Error',
                          description:
                            'Please select either a PM or HOD to assign this staff member under.',
                          variant: 'destructive',
                        });
                        return;
                      }
                    } else if (
                      memberForm.role === 'gm' &&
                      memberForm.cityId === 'none'
                    ) {
                      toast({
                        title: 'Error',
                        description: 'City is required for GM role',
                        variant: 'destructive',
                      });
                      return;
                    } else if (
                      (memberForm.role === 'pm' ||
                        memberForm.role === 'hod' ||
                        memberForm.role === 'dpm') &&
                      memberForm.cityIds.length === 0
                    ) {
                      toast({
                        title: 'Error',
                        description: `At least one city is required for ${memberForm.role.toUpperCase()} role`,
                        variant: 'destructive',
                      });
                      return;
                    }
                    const finalForm = { ...memberForm };
                    if (
                      isManager &&
                      myTeamId &&
                      finalForm.teamId !== myTeamId
                    ) {
                      // Force team to manager's team
                      finalForm.teamId = myTeamId!;
                      setMemberForm(prev => ({ ...prev, teamId: myTeamId! }));
                    }
                    createMemberMutation.mutate(finalForm);
                  }}
                  disabled={
                    createMemberMutation.isPending ||
                    (debouncedUsernameCheck.length >= 3 &&
                      usernameAvailability &&
                      !usernameAvailability.available)
                  }
                >
                  {createMemberMutation.isPending
                    ? 'Creating...'
                    : 'Create Member'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Team Project Modal */}
          <Dialog
            open={isEditTeamModalOpen}
            onOpenChange={setIsEditTeamModalOpen}
          >
            <DialogContent className="max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Edit Team Project</DialogTitle>
                <DialogDescription>
                  Update team project information
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                <div>
                  <Label htmlFor="edit-team-name">Project Name</Label>
                  <Input
                    id="edit-team-name"
                    value={teamForm.name}
                    onChange={e =>
                      setTeamForm({ ...teamForm, name: e.target.value })
                    }
                    placeholder="Enter Project name"
                    data-testid="input-edit-team-name"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-team-description">Description</Label>
                  <Input
                    id="edit-team-description"
                    value={teamForm.description}
                    onChange={e =>
                      setTeamForm({ ...teamForm, description: e.target.value })
                    }
                    placeholder="Project description (optional)"
                    data-testid="input-edit-team-description"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-team-city">City</Label>
                  <Select
                    value={teamForm.cityId}
                    onValueChange={value => {
                      // Clear PMs, GM, and HOD when city changes
                      setTeamForm({
                        ...teamForm,
                        cityId: value,
                        pmIds: [],
                        pmId: '',
                        managerId: '',
                        gmId: '',
                        hodIds: [],
                        hodId: '',
                        dpmIds: [],
                        dpmId: '',
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a city (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(city => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-team-pms">
                    Project Managers (PM) - Select Multiple
                    {!teamForm.cityId && (
                      <span className="text-gray-500 text-xs ml-2">
                        (Select city first)
                      </span>
                    )}
                  </Label>
                  <div className="border rounded-md p-3 max-h-48 overflow-auto">
                    {!teamForm.cityId ? (
                      <p className="text-sm text-gray-500 italic">
                        Please select a city above to see available Project
                        Managers
                      </p>
                    ) : (
                      (() => {
                        // Filter PMs: show PMs that have this city in their cityIds array OR have this city as their cityId
                        const availablePMs = users
                          .filter(u => u.role === 'pm')
                          .filter(u => {
                            // Check if PM has this city in their cityIds array (for PMs with multiple cities)
                            const pmCityIds = Array.isArray((u as any).cityIds)
                              ? (u as any).cityIds
                              : u.cityId
                                ? [u.cityId]
                                : [];
                            return pmCityIds.includes(teamForm.cityId);
                          })
                          .sort((a, b) => {
                            const nameA = `${a.firstName} ${a.lastName}`;
                            const nameB = `${b.firstName} ${b.lastName}`;
                            return nameA.localeCompare(nameB);
                          });

                        if (availablePMs.length === 0) {
                          return (
                            <p className="text-sm text-gray-500">
                              No PMs available for the selected city
                            </p>
                          );
                        }

                        return availablePMs.map(pm => {
                          const pmCityIds = Array.isArray((pm as any).cityIds)
                            ? (pm as any).cityIds
                            : pm.cityId
                              ? [pm.cityId]
                              : [];
                          const pmCityNames = pmCityIds
                            .map(
                              (id: string) =>
                                cities.find(c => c.id === id)?.name
                            )
                            .filter(Boolean)
                            .join(', ');

                          return (
                            <div
                              key={pm.id}
                              className="flex items-center space-x-2 py-1"
                            >
                              <input
                                type="checkbox"
                                id={`edit-pm-${pm.id}`}
                                checked={teamForm.pmIds.includes(pm.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setTeamForm({
                                      ...teamForm,
                                      pmIds: [...teamForm.pmIds, pm.id],
                                      pmId: pm.id, // Keep for backward compatibility
                                      managerId: pm.id,
                                    });
                                  } else {
                                    setTeamForm({
                                      ...teamForm,
                                      pmIds: teamForm.pmIds.filter(
                                        id => id !== pm.id
                                      ),
                                      pmId:
                                        teamForm.pmIds.length === 1
                                          ? ''
                                          : teamForm.pmId,
                                      managerId:
                                        teamForm.pmIds.length === 1
                                          ? ''
                                          : teamForm.managerId,
                                    });
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              <label
                                htmlFor={`edit-pm-${pm.id}`}
                                className="text-sm cursor-pointer flex-1"
                              >
                                {pm.firstName} {pm.lastName} (PM)
                                {pmCityNames && (
                                  <span className="text-gray-500 ml-1">
                                    - {pmCityNames}
                                  </span>
                                )}
                              </label>
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                </div>

                {/* GM Selection Section in Edit Modal */}
                {teamForm.cityId ? (
                  <div>
                    <Label htmlFor="edit-team-gm">
                      General Manager (GM) - Include in Project
                    </Label>
                    <div className="border rounded-md p-3 max-h-48 overflow-auto">
                      {(() => {
                        // Filter GMs: show GMs that have this city as their cityId
                        const availableGMs = users
                          .filter(u => u.role === 'gm')
                          .filter(u => u.cityId === teamForm.cityId)
                          .sort((a, b) => {
                            const nameA = `${a.firstName} ${a.lastName}`;
                            const nameB = `${b.firstName} ${b.lastName}`;
                            return nameA.localeCompare(nameB);
                          });

                        if (availableGMs.length === 0) {
                          return (
                            <p className="text-sm text-gray-500">
                              No GMs available for the selected city
                            </p>
                          );
                        }

                        return availableGMs.map(gm => (
                          <div
                            key={gm.id}
                            className="flex items-center space-x-2 py-1"
                          >
                            <input
                              type="checkbox"
                              id={`edit-gm-${gm.id}`}
                              checked={teamForm.gmId === gm.id}
                              onChange={e => {
                                if (e.target.checked) {
                                  setTeamForm({
                                    ...teamForm,
                                    gmId: gm.id,
                                  });
                                } else {
                                  setTeamForm({
                                    ...teamForm,
                                    gmId: '',
                                  });
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <label
                              htmlFor={`edit-gm-${gm.id}`}
                              className="text-sm cursor-pointer flex-1"
                            >
                              {gm.firstName} {gm.lastName} (GM)
                              {gm.cityId && (
                                <span className="text-gray-500 ml-1">
                                  -{' '}
                                  {cities.find(c => c.id === gm.cityId)?.name ||
                                    ''}
                                </span>
                              )}
                            </label>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    Please select a city above to see available General Managers
                  </p>
                )}

                {/* HOD + DPM Selection in Edit Modal */}
                {teamForm.cityId ? (
                  <>
                    <div>
                      <Label htmlFor="edit-team-hod">
                        Head of Department (HOD) - Include in Project
                      </Label>
                      <div className="border rounded-md p-3 max-h-48 overflow-auto">
                        {(() => {
                          const usersInCity = (role: 'hod' | 'dpm') =>
                            users
                              .filter(u => u.role === role)
                              .filter(u => {
                                const hodCityIds = Array.isArray(
                                  (u as any).cityIds
                                )
                                  ? (u as any).cityIds
                                  : u.cityId
                                    ? [u.cityId]
                                    : [];
                                return hodCityIds.includes(teamForm.cityId);
                              })
                              .sort((a, b) => {
                                const nameA = `${a.firstName} ${a.lastName}`;
                                const nameB = `${b.firstName} ${b.lastName}`;
                                return nameA.localeCompare(nameB);
                              });

                          const availableHodOnly = usersInCity('hod');

                          if (availableHodOnly.length === 0) {
                            return (
                              <p className="text-sm text-gray-500">
                                No HODs available for the selected city
                              </p>
                            );
                          }

                          const allHodSelected =
                            availableHodOnly.length > 0 &&
                            availableHodOnly.every(h =>
                              teamForm.hodIds.includes(h.id)
                            );

                          return (
                            <>
                              <div className="flex items-center space-x-2 py-1 border-b mb-2 pb-2">
                                <input
                                  type="checkbox"
                                  id="edit-hod-select-all"
                                  checked={allHodSelected}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      const merged = [
                                        ...teamForm.hodIds.filter(
                                          id =>
                                            !availableHodOnly.some(
                                              h => h.id === id
                                            )
                                        ),
                                        ...availableHodOnly.map(h => h.id),
                                      ];
                                      setTeamForm({
                                        ...teamForm,
                                        hodIds: merged,
                                        hodId: merged[0] || '',
                                      });
                                    } else {
                                      const keep = teamForm.hodIds.filter(
                                        id =>
                                          !availableHodOnly.some(
                                            h => h.id === id
                                          )
                                      );
                                      setTeamForm({
                                        ...teamForm,
                                        hodIds: keep,
                                        hodId: keep[0] || '',
                                      });
                                    }
                                  }}
                                  className="h-4 w-4"
                                />
                                <label
                                  htmlFor="edit-hod-select-all"
                                  className="text-sm cursor-pointer flex-1 font-medium"
                                >
                                  Select All (HOD)
                                </label>
                              </div>
                              {availableHodOnly.map(hod => {
                                const hodCityIds = Array.isArray(
                                  (hod as any).cityIds
                                )
                                  ? (hod as any).cityIds
                                  : hod.cityId
                                    ? [hod.cityId]
                                    : [];
                                const hodCityNames = hodCityIds
                                  .map(
                                    (id: string) =>
                                      cities.find(c => c.id === id)?.name
                                  )
                                  .filter(Boolean)
                                  .join(', ');

                                return (
                                  <div
                                    key={hod.id}
                                    className="flex items-center space-x-2 py-1"
                                  >
                                    <input
                                      type="checkbox"
                                      id={`edit-hod-${hod.id}`}
                                      checked={teamForm.hodIds.includes(hod.id)}
                                      onChange={e => {
                                        if (e.target.checked) {
                                          setTeamForm({
                                            ...teamForm,
                                            hodIds: [
                                              ...teamForm.hodIds,
                                              hod.id,
                                            ],
                                            hodId:
                                              teamForm.hodIds.length === 0
                                                ? hod.id
                                                : teamForm.hodId,
                                          });
                                        } else {
                                          setTeamForm({
                                            ...teamForm,
                                            hodIds: teamForm.hodIds.filter(
                                              id => id !== hod.id
                                            ),
                                            hodId:
                                              teamForm.hodIds.length === 1
                                                ? ''
                                                : teamForm.hodId,
                                          });
                                        }
                                      }}
                                      className="h-4 w-4"
                                    />
                                    <label
                                      htmlFor={`edit-hod-${hod.id}`}
                                      className="text-sm cursor-pointer flex-1"
                                    >
                                      {hod.firstName} {hod.lastName} (HOD)
                                      {hodCityNames && (
                                        <span className="text-gray-500 ml-1">
                                          - {hodCityNames}
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="edit-team-dpm">
                        Deputy Project Manager (DPM) - Include in Project
                      </Label>
                      <div className="border rounded-md p-3 max-h-48 overflow-auto">
                        {(() => {
                          const usersInCity = (role: 'hod' | 'dpm') =>
                            users
                              .filter(u => u.role === role)
                              .filter(u => {
                                const ids = Array.isArray((u as any).cityIds)
                                  ? (u as any).cityIds
                                  : u.cityId
                                    ? [u.cityId]
                                    : [];
                                return ids.includes(teamForm.cityId);
                              })
                              .sort((a, b) => {
                                const nameA = `${a.firstName} ${a.lastName}`;
                                const nameB = `${b.firstName} ${b.lastName}`;
                                return nameA.localeCompare(nameB);
                              });

                          const availableDpmOnly = usersInCity('dpm');

                          if (availableDpmOnly.length === 0) {
                            return (
                              <p className="text-sm text-gray-500">
                                No DPMs available for the selected city
                              </p>
                            );
                          }

                          const allDpmSelected =
                            availableDpmOnly.length > 0 &&
                            availableDpmOnly.every(d =>
                              teamForm.dpmIds.includes(d.id)
                            );

                          return (
                            <>
                              <div className="flex items-center space-x-2 py-1 border-b mb-2 pb-2">
                                <input
                                  type="checkbox"
                                  id="edit-dpm-select-all"
                                  checked={allDpmSelected}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      const merged = [
                                        ...teamForm.dpmIds.filter(
                                          id =>
                                            !availableDpmOnly.some(
                                              d => d.id === id
                                            )
                                        ),
                                        ...availableDpmOnly.map(d => d.id),
                                      ];
                                      setTeamForm({
                                        ...teamForm,
                                        dpmIds: merged,
                                        dpmId: merged[0] || '',
                                      });
                                    } else {
                                      const keep = teamForm.dpmIds.filter(
                                        id =>
                                          !availableDpmOnly.some(
                                            d => d.id === id
                                          )
                                      );
                                      setTeamForm({
                                        ...teamForm,
                                        dpmIds: keep,
                                        dpmId: keep[0] || '',
                                      });
                                    }
                                  }}
                                  className="h-4 w-4"
                                />
                                <label
                                  htmlFor="edit-dpm-select-all"
                                  className="text-sm cursor-pointer flex-1 font-medium"
                                >
                                  Select All (DPM)
                                </label>
                              </div>
                              {availableDpmOnly.map(dpm => {
                                const dpmCityIds = Array.isArray(
                                  (dpm as any).cityIds
                                )
                                  ? (dpm as any).cityIds
                                  : dpm.cityId
                                    ? [dpm.cityId]
                                    : [];
                                const dpmCityNames = dpmCityIds
                                  .map(
                                    (id: string) =>
                                      cities.find(c => c.id === id)?.name
                                  )
                                  .filter(Boolean)
                                  .join(', ');

                                return (
                                  <div
                                    key={dpm.id}
                                    className="flex items-center space-x-2 py-1"
                                  >
                                    <input
                                      type="checkbox"
                                      id={`edit-dpm-${dpm.id}`}
                                      checked={teamForm.dpmIds.includes(dpm.id)}
                                      onChange={e => {
                                        if (e.target.checked) {
                                          setTeamForm({
                                            ...teamForm,
                                            dpmIds: [
                                              ...teamForm.dpmIds,
                                              dpm.id,
                                            ],
                                            dpmId:
                                              teamForm.dpmIds.length === 0
                                                ? dpm.id
                                                : teamForm.dpmId,
                                          });
                                        } else {
                                          setTeamForm({
                                            ...teamForm,
                                            dpmIds: teamForm.dpmIds.filter(
                                              id => id !== dpm.id
                                            ),
                                            dpmId:
                                              teamForm.dpmIds.length === 1
                                                ? ''
                                                : teamForm.dpmId,
                                          });
                                        }
                                      }}
                                      className="h-4 w-4"
                                    />
                                    <label
                                      htmlFor={`edit-dpm-${dpm.id}`}
                                      className="text-sm cursor-pointer flex-1"
                                    >
                                      {dpm.firstName} {dpm.lastName} (DPM)
                                      {dpmCityNames && (
                                        <span className="text-gray-500 ml-1">
                                          - {dpmCityNames}
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    Please select a city above to see available HOD and DPM
                    options
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditTeamModalOpen(false);
                    setEditingTeam(null);
                    setTeamForm({
                      name: '',
                      description: '',
                      managerId: '',
                      pmId: '',
                      cityId: '',
                      pmIds: [],
                      gmId: '',
                      hodIds: [],
                      hodId: '',
                      dpmIds: [],
                      dpmId: '',
                    });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditTeamSubmit}
                  disabled={editTeamMutation.isPending}
                  data-testid="button-submit-edit-team"
                >
                  {editTeamMutation.isPending ? 'Updating...' : 'Update Team'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
